import { useState, useRef, useEffect, useMemo, Fragment } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ChartLineUp, Robot, Sparkle, FileText, FilePdf, FileXls,
  Warning, CurrencyEur, Calendar, Buildings, Tag, Percent, Clock, 
  ArrowUp, ArrowDown, DownloadSimple, Funnel, CalendarBlank, CaretDown, CaretUp, CaretRight, Cube,
  MagnifyingGlass, MapPin, Gear, ArrowsClockwise, TrendUp, TrendDown, Scales, Target, Question, Star, Diamond
} from "@phosphor-icons/react";
import { Tooltip as UiTooltip, TooltipContent as UiTooltipContent, TooltipProvider as UiTooltipProvider, TooltipTrigger as UiTooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/apiError";
import { cleanProductName, toSentenceCase, shortenCompanyName } from "@/lib/textFormat";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import { useSettings } from "@/contexts/SettingsContext";

// Nome a mostrar para um "customer" de sales_by_customer: prioriza o nome comercial exato
// da ficha do cliente (commercial_name, cruzado no backend - maiúsculas/minúsculas tal como
// estão na ficha), com reserva no nome do documento de origem (PDF/Excel) já sem o sufixo
// legal (Lda, S.A., Unipessoal).
const displayCustomerName = (c) => {
  if (!c) return '';
  return c.commercial_name || shortenCompanyName(c.customer_full || c.customer || '');
};
import { PageHeader } from "@/components/PageHeader";
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
  ScatterChart, Scatter, ZAxis, ReferenceArea, ReferenceLine, Cell
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chart colors
const COLORS = ['#009392', '#00b3b0', '#72c7c3', '#a8dbd9', '#d4eeee', '#005f5f', '#003d3d', '#00c7c4', '#4dd4d1', '#99e6e4'];
const QUARTER_COLORS = ['#009392', '#f59e0b', '#8b5cf6', '#ec4899'];

// Month to quarter mapping
const MONTH_TO_QUARTER = {
  'Janeiro': 'Q1', 'Fevereiro': 'Q1', 'Março': 'Q1',
  'Abril': 'Q2', 'Maio': 'Q2', 'Junho': 'Q2',
  'Julho': 'Q3', 'Agosto': 'Q3', 'Setembro': 'Q3',
  'Outubro': 'Q4', 'Novembro': 'Q4', 'Dezembro': 'Q4'
};

const QUARTER_MONTHS = {
  'Q1': ['Janeiro', 'Fevereiro', 'Março'],
  'Q2': ['Abril', 'Maio', 'Junho'],
  'Q3': ['Julho', 'Agosto', 'Setembro'],
  'Q4': ['Outubro', 'Novembro', 'Dezembro']
};

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(value);
};

// Format number with K/M suffix
const formatShortCurrency = (value) => {
  if (value >= 1000000) return `€${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `€${(value / 1000).toFixed(0)}K`;
  return `€${value.toFixed(0)}`;
};

// Truncate text for chart labels
const truncateText = (text, maxLen = 25) => {
  if (!text) return '';
  const cleaned = text.replace(/ - /g, '-').replace(/\s+/g, ' ').trim();
  if (cleaned.length <= maxLen) return cleaned;
  return cleaned.substring(0, maxLen - 2) + '..'
};

// Extração de Cor e Medida a partir da descrição/material do produto (ex: "GA 0515 ROD OAK
// BROWN 9,5X120 R") - usada para agrupar "Vendas por Cor" e "Vendas por Medida" sem precisar
// de nenhum campo novo no backend. Dicionário de sinónimos normaliza variações do mesmo tom
// (ex: BEIGE/BEGE, WHITE/BRANCO, GREY/CINZA) para um único rótulo em português.
const COLOR_SYNONYMS = {
  BEGE: 'Bege', BEIGE: 'Bege',
  BRANCO: 'Branco', WHITE: 'Branco',
  CREME: 'Creme', CREAM: 'Creme',
  CINZA: 'Cinza', CINZAS: 'Cinza', GREY: 'Cinza', GRAY: 'Cinza', GRIS: 'Cinza',
  CASTANHO: 'Castanho', BROWN: 'Castanho',
  PRETO: 'Preto', BLACK: 'Preto', NEGRO: 'Preto',
  VERDE: 'Verde', GREEN: 'Verde',
  AZUL: 'Azul', BLUE: 'Azul',
  ROSA: 'Rosa', PINK: 'Rosa',
  AMARELO: 'Amarelo', YELLOW: 'Amarelo',
  VERMELHO: 'Vermelho', RED: 'Vermelho',
  NATURAL: 'Natural',
  TAUPE: 'Taupe',
  ANTRACITE: 'Antracite', ANTHRACITE: 'Antracite',
  DARK: 'Escuro', ESCURO: 'Escuro',
  LIGHT: 'Claro', CLARO: 'Claro',
  MARFIM: 'Marfim', IVORY: 'Marfim',
  DOURADO: 'Dourado', GOLD: 'Dourado',
};

const extractColor = (material) => {
  if (!material) return null;
  const words = String(material).toUpperCase().split(/[\s\-_/]+/);
  for (const w of words) {
    const clean = w.replace(/[^A-Z]/g, '');
    if (COLOR_SYNONYMS[clean]) return COLOR_SYNONYMS[clean];
  }
  return null;
};

const extractSize = (material) => {
  if (!material) return null;
  const m = String(material).match(/(\d+(?:[.,]\d+)?)\s*[xX]\s*(\d+(?:[.,]\d+)?)/);
  if (!m) return null;
  return `${m[1].replace('.', ',')}x${m[2].replace('.', ',')}`;
};

// Classificação de temperatura de cor (Quente/Frio/Neutro) - baseada na tabela real fornecida
// pelo cliente (nomes de cores/acabamentos do catálogo Gresart), não numa teoria de cores
// genérica. Frases de 2 palavras são verificadas ANTES das palavras isoladas (ordem importa:
// ex. "Dark Beige" é Quente, mas "Dark" isolado é Neutro; "Mix Grey"/"Mix Cinza" é Frio, mas
// "Mix" isolado é Neutro).
const COLOR_TEMPERATURE_RULES = [
  // Frases de 2 palavras
  ['DARK BEIGE', 'Quente'],
  ['LIGHT GREY', 'Frio'], ['MIX GREY', 'Frio'], ['MIX CINZA', 'Frio'],
  ['GREY 1', 'Frio'], ['GREY 6', 'Frio'], ['GREY 2', 'Frio'], ['GREY 4', 'Frio'],
  ['BRANCO BRILHO', 'Neutro'], ['BRANCO MATE', 'Neutro'], ['MIX COLOUR', 'Neutro'],
  ['COLOUR 1', 'Neutro'], ['COLOUR 6', 'Neutro'], ['COLOUR 2', 'Neutro'], ['COLOUR 4', 'Neutro'],
  // Palavras isoladas - Cores Quentes
  ['SAND', 'Quente'], ['CARAMEL', 'Quente'], ['AUTUMN', 'Quente'], ['MERLOT', 'Quente'],
  ['CREAM', 'Quente'], ['BEIGE', 'Quente'], ['BEGE', 'Quente'], ['TORTORA', 'Quente'],
  ['HONEY', 'Quente'], ['BROWN', 'Quente'], ['NUT', 'Quente'], ['WENGUE', 'Quente'],
  ['GOLD', 'Quente'], ['LARANJA', 'Quente'], ['OXYD', 'Quente'], ['REAL', 'Quente'],
  ['STAIN', 'Quente'], ['MULTICOLOR', 'Quente'], ['PEROLA', 'Quente'],
  // Palavras isoladas - Cores Frias
  ['FOG', 'Frio'], ['MINT', 'Frio'], ['AQUA', 'Frio'], ['COBALT', 'Frio'], ['JADE', 'Frio'],
  ['BLUE', 'Frio'], ['SILVER', 'Frio'], ['LIGHT', 'Frio'], ['ASH', 'Frio'], ['GREY', 'Frio'],
  ['GRAFITE', 'Frio'], ['CINZA', 'Frio'], ['ESTANHO', 'Frio'], ['SMOKE', 'Frio'],
  ['ANTRACITE', 'Frio'], ['SLATE', 'Frio'],
  // Palavras isoladas - Cores Neutras
  ['BRANCO', 'Neutro'], ['WHITE', 'Neutro'], ['BLACK', 'Neutro'], ['PRETO', 'Neutro'],
  ['DARK', 'Neutro'], ['CAL', 'Neutro'], ['BLEND', 'Neutro'], ['MIX', 'Neutro'],
  ['VINTAGE', 'Neutro'], ['LONDON', 'Neutro'], ['PRAGAL', 'Neutro'], ['PARDO', 'Neutro'],
];

const normalizeForTemperatureMatch = (s) => {
  if (!s) return '';
  return String(s)
    .toUpperCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove acentos (ex: Pérola -> PEROLA)
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
};

const extractColorTemperature = (material) => {
  const norm = normalizeForTemperatureMatch(material);
  if (!norm) return null;
  for (const [phrase, category] of COLOR_TEMPERATURE_RULES) {
    const re = new RegExp(`\\b${phrase.replace(' ', '\\s+')}\\b`);
    if (re.test(norm)) return category;
  }
  return null;
};

const TEMPERATURE_META = {
  Quente: { color: '#e07a3e', badge: 'bg-orange-100 text-orange-700' },
  Frio: { color: '#3e8fe0', badge: 'bg-sky-100 text-sky-700' },
  Neutro: { color: '#9c9585', badge: 'bg-stone-200 text-stone-700' },
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const displayLabel = data.commercial_name || data.customer_full || (data.collection && toSentenceCase(data.collection)) || (data.material && cleanProductName(data.material)) || label;
    return (
      <div className="bg-white border border-stone-200 shadow-lg rounded-lg p-3 max-w-xs">
        <p className="font-medium text-stone-800 text-sm">{displayLabel}</p>
        <p className="text-[#009392] font-semibold">
          Vendas: {formatCurrency(payload[0].value)}
        </p>
        {data.pending_value > 0 && (
          <p className="text-amber-600 font-semibold">
            Pendentes: {formatCurrency(data.pending_value)}
          </p>
        )}
        {data.yoy_change !== null && data.yoy_change !== undefined && (
          <p className={`font-medium ${data.yoy_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            YoY: {data.yoy_change >= 0 ? '+' : ''}{data.yoy_change}%
          </p>
        )}
      </div>
    );
  }
  return null;
};

// Tooltip da Matriz de Saúde da Carteira (bubble chart) - nome do cliente e os 3 valores
// exatos (Volume m², Preço Médio €/m², Valor Faturado €)
const BubbleMatrixTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-stone-200 shadow-lg rounded-lg p-3 max-w-xs">
        <p className="font-medium text-stone-800 text-sm mb-1">{data.commercial_name || data.customer_full}</p>
        <p className="text-teal-600 text-sm">Volume: <span className="font-semibold">{data.m2.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²</span></p>
        <p className="text-sky-600 text-sm">Preço Médio: <span className="font-semibold">{formatCurrency(data.avg_price)}/m²</span></p>
        <p className="text-stone-600 text-sm">Valor Faturado: <span className="font-semibold">{formatCurrency(data.sales)}</span></p>
      </div>
    );
  }
  return null;
};

// YoY Badge component
const YoYBadge = ({ value, size = "sm" }) => {
  if (value === null || value === undefined) return null;
  const isPositive = value >= 0;
  const sizeClasses = size === "lg" 
    ? "px-2 py-1 text-sm" 
    : "px-1.5 py-0.5 text-xs";
  return (
    <span className={`inline-flex items-center gap-0.5 ${sizeClasses} rounded font-medium ${
      isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
    }`}>
      {isPositive ? <ArrowUp size={size === "lg" ? 14 : 12} /> : <ArrowDown size={size === "lg" ? 14 : 12} />}
      {isPositive ? '+' : ''}{value}%
    </span>
  );
};

// Cabeçalho de tabela clicável e reutilizável (tabelas de Clientes/Séries/Produtos em
// Análise de Vendas) - 1º clique ordena maior→menor, 2º clique menor→maior, 3º clique
// volta à ordem original (por Vendas, tal como o backend já devolve).
const SortableTh = ({ label, sortKey, sortState, onSort, colorClass = "text-stone-600", testId, align = "right" }) => {
  const isActive = sortState?.key === sortKey;
  return (
    <th className={`text-${align} py-2 px-1.5 sm:px-3 font-medium ${colorClass}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 hover:underline"
        data-testid={testId}
      >
        {label}
        {isActive && (sortState.dir === 'desc' ? <CaretDown size={12} weight="bold" /> : <CaretUp size={12} weight="bold" />)}
      </button>
    </th>
  );
};

// Cruza dados do Hit Parade (vendas faturadas) com Open Orders (encomendas pendentes) para
// mostrar 3 sinais de inteligência comercial por cliente, calculados no backend
// (_merge_pending_orders em server.py): saúde de stock (% Ord 1º da fábrica), aceleração
// (pedido atual vs. a média mensal habitual desse cliente) e risco de
// concentração/dependência (>20% das vendas totais faturadas vêm deste único cliente).
const IndicadoresCell = ({ item, testId }) => {
  const { stock_health, pct_ord_1, acceleration, is_concentration_risk, concentration_pct } = item;
  if (!stock_health && !acceleration && !is_concentration_risk) {
    return <span className="text-stone-300">—</span>;
  }
  const healthColor = { green: '#16a34a', yellow: '#d97706', red: '#dc2626' }[stock_health];
  return (
    <div className="flex items-center justify-end gap-1.5" data-testid={testId}>
      {stock_health && (
        <span
          className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
          style={{ backgroundColor: healthColor }}
          title={`Índice de Saúde de Stock: ${pct_ord_1}% Ord 1º${stock_health === 'red' ? ' — fábrica com falhas de entrega' : stock_health === 'yellow' ? ' — entregas parciais frequentes' : ' — fábrica a entregar bem'}`}
        />
      )}
      {acceleration === 'up' && (
        <TrendUp size={15} weight="bold" className="text-green-600 flex-shrink-0" title="Aceleração: pedido atual acima da média mensal (Crescimento)" />
      )}
      {acceleration === 'down' && (
        <TrendDown size={15} weight="bold" className="text-red-500 flex-shrink-0" title="Aceleração: pedido atual bem abaixo da média mensal (Abrandamento)" />
      )}
      {is_concentration_risk && (
        <Scales size={15} weight="fill" className="text-purple-500 flex-shrink-0" title={`Risco de Concentração/Dependência: ${concentration_pct}% das vendas totais vêm deste cliente`} />
      )}
    </div>
  );
};

// Conteúdo da legenda da coluna "Sinais", partilhado entre o tooltip do cabeçalho da tabela
// (ícone "?", sem ocupar espaço permanente) - mesmos ícones/cores reais usados na coluna.
const SinaisLegendContent = () => (
  <div className="space-y-1.5 text-[11px]">
    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full inline-block bg-green-600 flex-shrink-0" /> Stock Saudável (Serviço &gt;80%)</div>
    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full inline-block bg-amber-600 flex-shrink-0" /> Stock com Atraso (50-80%)</div>
    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full inline-block bg-red-600 flex-shrink-0" /> Falta de Stock Crítica (&lt;50%)</div>
    <div className="flex items-center gap-1.5"><TrendUp size={12} weight="bold" className="text-green-500 flex-shrink-0" /> Crescimento (pedido acima da média)</div>
    <div className="flex items-center gap-1.5"><TrendDown size={12} weight="bold" className="text-red-400 flex-shrink-0" /> Abrandamento (pedido abaixo da média)</div>
    <div className="flex items-center gap-1.5"><Scales size={12} weight="fill" className="text-purple-400 flex-shrink-0" /> Risco de Concentração (&gt;20% das vendas)</div>
  </div>
);

// Wraps a long axis label into up to 2 lines (split near the middle at a word boundary)
const wrapTickText = (text, maxCharsPerLine = 16) => {
  const value = text || '';
  if (value.length <= maxCharsPerLine) return [value];
  const words = value.split(' ');
  let line1 = '';
  let line2 = '';
  for (const w of words) {
    if (!line1 || (line1 + ' ' + w).trim().length <= maxCharsPerLine) {
      line1 = (line1 + ' ' + w).trim();
    } else {
      line2 = (line2 + ' ' + w).trim();
    }
  }
  if (line2.length > maxCharsPerLine) {
    line2 = line2.slice(0, maxCharsPerLine - 1) + '…';
  }
  return line2 ? [line1, line2] : [line1];
};

// Custom YAxis tick that wraps long product/série names into 2 lines instead of crowding
const WrappedYAxisTick = ({ x, y, payload }) => {
  const lines = wrapTickText(payload.value, 16);
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={lines.length > 1 ? -3 : 4} textAnchor="end" fill="#6b7280" fontSize={11}>
        {lines.map((line, i) => (
          <tspan key={i} x={0} dy={i === 0 ? 0 : 13}>{line}</tspan>
        ))}
      </text>
    </g>
  );
};

// Reusable row showing one product: material, M2, average price, total sales
const ProductRow = ({ product, testId }) => (
  <div
    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-3 py-1.5 px-2.5 bg-white rounded-lg border border-stone-100"
    data-testid={testId}
  >
    <div className="flex items-center gap-2 min-w-0">
      <Cube size={14} className="text-[#009392] flex-shrink-0" />
      <span className="text-xs text-stone-700 truncate" title={product.material}>{cleanProductName(product.material)}</span>
    </div>
    <div className="flex items-center gap-3 flex-wrap pl-5 sm:pl-0 sm:flex-nowrap sm:flex-shrink-0">
      {product.qty !== null && product.qty !== undefined && (
        <span className="text-xs text-stone-500 whitespace-nowrap">
          {product.qty.toLocaleString('pt-PT', { maximumFractionDigits: 1 })} m²
        </span>
      )}
      {product.avg_price !== null && product.avg_price !== undefined && (
        <span className="text-xs text-stone-500 whitespace-nowrap">
          <strong className="text-stone-600">{formatCurrency(product.avg_price)}</strong>/m²
        </span>
      )}
      <span className="text-xs font-semibold text-[#009392] whitespace-nowrap sm:min-w-[68px] sm:text-right">
        {formatCurrency(product.sales)}
      </span>
    </div>
  </div>
);

// Normalize text for accent/case-insensitive search
const normalizeSearch = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function SalesAnalysis() {
  const { isReadOnly } = useSettings();
  const [loading, setLoading] = useState(true);
  
  // VENDAS state
  const [vendasData, setVendasData] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [colorTemperatureHistory, setColorTemperatureHistory] = useState([]);
  const [monthlyGoal, setMonthlyGoal] = useState(null); // "Objetivo Mensal" manual (Configurações), usado na "Previsão de Fecho" quando o PDF não tem target
  const [selectedTemperature, setSelectedTemperature] = useState(null); // 'Quente' | 'Frio' | 'Neutro' | null
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  
  // Period filter state
  const [selectedPeriod, setSelectedPeriod] = useState('all'); // 'all', 'Q1', 'Q2', 'Q3', 'Q4'

  // Expanded "top products" state per customer: { [customer_full]: 'closed' | 'top3' | 'all' }
  const [expandedCustomerProducts, setExpandedCustomerProducts] = useState({});

  const toggleCustomerProducts = (customerKey) => {
    setExpandedCustomerProducts(prev => ({
      ...prev,
      [customerKey]: (prev[customerKey] || 'closed') === 'closed' ? 'top3' : 'closed'
    }));
  };

  const showAllProducts = (customerKey) => {
    setExpandedCustomerProducts(prev => ({ ...prev, [customerKey]: 'all' }));
  };

  // Single-Client search state
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [selectedClientDetail, setSelectedClientDetail] = useState(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // M2 + average price for the client currently open in the detail dialog
  const selectedClientM2 = useMemo(() => {
    if (!selectedClientDetail?.top_products) return { m2: 0, avgPrice: null };
    const m2 = selectedClientDetail.top_products.reduce((sum, p) => sum + (p.qty || 0), 0);
    const avgPrice = m2 > 0 ? selectedClientDetail.sales / m2 : null;
    return { m2, avgPrice };
  }, [selectedClientDetail]);

  const clientSearchResults = useMemo(() => {
    if (!clientSearchQuery.trim() || !vendasData?.sales_by_customer) return [];
    const q = normalizeSearch(clientSearchQuery);
    return vendasData.sales_by_customer
      .filter(c => normalizeSearch(c.customer_full || c.customer).includes(q) || normalizeSearch(c.commercial_name || '').includes(q))
      .slice(0, 8);
  }, [clientSearchQuery, vendasData]);

  // Pesquisa por Série - lista os clientes que compraram uma série (ex: "Oak") no ano
  // selecionado, somando o m2/vendas dessa série a partir do top_products de cada cliente
  // (campo 'collection' já normalizado para a raiz da série pelo backend). O ano "2026" usa
  // qty/sales (ano corrente), "2025" usa prev_qty/prev_sales (ano anterior), "Todos" soma ambos.
  const [seriesSearchQuery, setSeriesSearchQuery] = useState('');
  const [seriesSearchYear, setSeriesSearchYear] = useState('all');

  const seriesSearchResults = useMemo(() => {
    const q = normalizeSearch(seriesSearchQuery.trim());
    if (!q || !vendasData?.sales_by_customer) return [];
    const results = [];
    for (const cust of vendasData.sales_by_customer) {
      let m2 = 0;
      let sales = 0;
      for (const p of (cust.top_products || [])) {
        if (!normalizeSearch(p.collection || '').includes(q)) continue;
        if (seriesSearchYear === '2026' || seriesSearchYear === 'all') {
          m2 += p.qty || 0;
          sales += p.sales || 0;
        }
        if (seriesSearchYear === '2025' || seriesSearchYear === 'all') {
          m2 += p.prev_qty || 0;
          sales += p.prev_sales || 0;
        }
      }
      if (m2 > 0 || sales > 0) {
        results.push({ ...cust, m2, sales });
      }
    }
    return results.sort((a, b) => b.m2 - a.m2);
  }, [seriesSearchQuery, seriesSearchYear, vendasData]);

  // Ordenação genérica das tabelas "Todos os Clientes", "Todas as Séries" e "Todos os
  // Produtos" - clicar num cabeçalho (Vendas, % Peso, M2, Preço Médio, YoY) ordena por essa
  // coluna (1º clique = maior→menor, 2º clique = menor→maior, 3º clique = volta à ordem
  // original). Clicar noutra coluna troca de imediato o critério ativo, sem ficar preso a
  // apenas uma coluna (bug reportado: não se conseguia voltar facilmente a ordenar por
  // Vendas depois de ordenar por % Peso).
  const [clientSort, setClientSort] = useState({ key: null, dir: null });
  const [serieSort, setSerieSort] = useState({ key: null, dir: null });
  const [productSort, setProductSort] = useState({ key: null, dir: null });
  const [colorSort, setColorSort] = useState({ key: null, dir: null });
  const [sizeSort, setSizeSort] = useState({ key: null, dir: null });

  const makeSortToggler = (setSortState) => (key) => {
    setSortState(prev => {
      if (prev.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      return { key: null, dir: null };
    });
  };
  const toggleClientSort = makeSortToggler(setClientSort);
  const toggleSerieSort = makeSortToggler(setSerieSort);
  const toggleProductSort = makeSortToggler(setProductSort);
  const toggleColorSort = makeSortToggler(setColorSort);
  const toggleSizeSort = makeSortToggler(setSizeSort);

  const sortRows = (list, sortState, getValue) => {
    if (!sortState?.key) return list;
    return [...list].sort((a, b) => {
      const va = getValue(a, sortState.key);
      const vb = getValue(b, sortState.key);
      if (typeof va === 'string' || typeof vb === 'string') {
        const cmp = String(va ?? '').localeCompare(String(vb ?? ''), 'pt', { sensitivity: 'base' });
        return sortState.dir === 'asc' ? cmp : -cmp;
      }
      const na = (va === null || va === undefined) ? -Infinity : va;
      const nb = (vb === null || vb === undefined) ? -Infinity : vb;
      const diff = na - nb;
      return sortState.dir === 'asc' ? diff : -diff;
    });
  };

  const sortedSalesByCustomer = useMemo(
    () => sortRows(vendasData?.sales_by_customer || [], clientSort, (item, key) => item[key]),
    [vendasData, clientSort]
  );
  const sortedSalesByCollection = useMemo(
    () => sortRows(vendasData?.sales_by_collection || [], serieSort, (item, key) => item[key]),
    [vendasData, serieSort]
  );


  // Matriz de Saúde da Carteira (Cuadrante Mágico): X=Volume (m²), Y=Preço Médio (€/m²),
  // tamanho da bolha=Valor Faturado (€). Os quadrantes dividem-se pela MEDIANA real dos
  // clientes carregados (não um valor fixo), para se adaptarem a qualquer carteira.
  const bubbleMatrixData = useMemo(() => {
    if (!vendasData?.sales_by_customer) return [];
    return vendasData.sales_by_customer
      .filter(c => (c.m2 || 0) > 0 && (c.avg_price || 0) > 0 && (c.sales || 0) > 0)
      .map(c => ({
        customer: c.customer,
        customer_full: displayCustomerName(c),
        m2: c.m2,
        avg_price: c.avg_price,
        sales: c.sales
      }));
  }, [vendasData]);

  const bubbleMatrixStats = useMemo(() => {
    if (bubbleMatrixData.length === 0) return null;
    const median = (arr) => {
      const sorted = [...arr].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    };
    const m2Values = bubbleMatrixData.map(d => d.m2);
    const priceValues = bubbleMatrixData.map(d => d.avg_price);
    return {
      medianM2: median(m2Values),
      medianPrice: median(priceValues),
      maxM2: Math.max(...m2Values) * 1.08,
      maxPrice: Math.max(...priceValues) * 1.08
    };
  }, [bubbleMatrixData]);

  const [selectedBubble, setSelectedBubble] = useState(null);
  const [activeQuadrant, setActiveQuadrant] = useState(null); // null | 'Estrelas' | 'Volume' | 'Joias' | 'Drenagem'
  const bubbleQuadrant = (d, stats) => {
    const highVolume = d.m2 > stats.medianM2;
    const highPrice = d.avg_price > stats.medianPrice;
    return highVolume && highPrice ? 'Estrelas' : highVolume && !highPrice ? 'Volume' : !highVolume && highPrice ? 'Joias' : 'Drenagem';
  };

  // Heat map by zone state
  const [heatmapData, setHeatmapData] = useState([]);
  const [loadingHeatmap, setLoadingHeatmap] = useState(false);
  const [heatmapUnmatched, setHeatmapUnmatched] = useState([]);
  const [heatmapTotals, setHeatmapTotals] = useState({ total: 0, matched: 0 });
  const [showUnmatchedClients, setShowUnmatchedClients] = useState(false);
  const [showFullCollectionsRanking, setShowFullCollectionsRanking] = useState(false);
  const [showFullConcentrationRanking, setShowFullConcentrationRanking] = useState(false);
  const loadHeatmap = async () => {
    setLoadingHeatmap(true);
    try {
      const response = await axios.get(`${API}/sales/heatmap-by-zone`);
      setHeatmapData(response.data?.zones || []);
      setHeatmapUnmatched(response.data?.unmatched || []);
      setHeatmapTotals({ total: response.data?.total_customers || 0, matched: response.data?.matched_customers || 0 });
    } catch (error) {
      console.error("Error loading heatmap:", error);
      toast.error("Erro ao carregar mapa de calor: " + getApiErrorMessage(error));
    } finally {
      setLoadingHeatmap(false);
    }
  };

  // "Desempenho Anual por Cliente" - cruza os PDFs 2024/2025 (vendas em €/vendas em M2) com
  // as vendas YTD de 2026 já existentes, calculado no backend (/sales/annual-performance).
  const [annualPerformance, setAnnualPerformance] = useState({ clients: [], has_eur_data: false, has_m2_data: false });
  const [loadingAnnualPerformance, setLoadingAnnualPerformance] = useState(true);
  const [annualPerfSearch, setAnnualPerfSearch] = useState("");
  const [annualPerfSort, setAnnualPerfSort] = useState({ key: 'sales_2025', dir: 'desc' });
  const toggleAnnualPerfSort = makeSortToggler(setAnnualPerfSort);
  const loadAnnualPerformance = async () => {
    setLoadingAnnualPerformance(true);
    try {
      const response = await axios.get(`${API}/sales/annual-performance`);
      setAnnualPerformance(response.data || { clients: [], has_eur_data: false, has_m2_data: false });
    } catch (error) {
      console.error("Error loading annual performance:", error);
    } finally {
      setLoadingAnnualPerformance(false);
    }
  };

  // Filtra por nome e ordena (SortableTh/sortRows já existentes) a tabela "Desempenho
  // Anual por Cliente".
  const filteredAnnualPerformance = useMemo(() => {
    const clients = annualPerformance?.clients || [];
    const q = normalizeSearch(annualPerfSearch.trim());
    const filtered = q ? clients.filter(c => normalizeSearch(c.name).includes(q) || normalizeSearch(c.commercial_name || '').includes(q)) : clients;
    return sortRows(filtered, annualPerfSort, (item, key) => item[key]);
  }, [annualPerformance, annualPerfSearch, annualPerfSort]);

  // Load saved VENDAS data on mount
  const loadVendasData = async () => {
    try {
      const response = await axios.get(`${API}/sales/vendas`);
      if (response.data && (response.data.monthly_sales?.length > 0 || response.data.sales_by_collection?.length > 0)) {
        setVendasData(response.data);
      }
    } catch (error) {
      console.error("Error loading VENDAS data:", error);
    } finally {
      setLoading(false);
    }
  };
  // Tendência Sazonal: histórico trimestral de Quente/Frio/Neutro, construído automaticamente
  // pelo backend a cada carregamento de vendas (não precisa de re-enviar ficheiros antigos).
  const loadColorTemperatureHistory = async () => {
    try {
      const response = await axios.get(`${API}/sales/color-temperature-history`);
      setColorTemperatureHistory(response.data?.snapshots || []);
    } catch (error) {
      console.error("Error loading color temperature history:", error);
    }
  };
  // Snapshot Manual: força a gravação do snapshot do trimestre atual já agora, sem esperar
  // pelo próximo carregamento de ficheiros de vendas.
  const handleForceSnapshot = async () => {
    setSavingSnapshot(true);
    try {
      const response = await axios.post(`${API}/sales/color-temperature-snapshot/force`);
      toast.success(`Snapshot de ${response.data.label} gravado com sucesso`);
      await loadColorTemperatureHistory();
    } catch (error) {
      toast.error("Erro ao gravar snapshot: " + getApiErrorMessage(error));
    } finally {
      setSavingSnapshot(false);
    }
  };
  useEffect(() => {
    loadVendasData();
    loadColorTemperatureHistory();
    axios.get(`${API}/sales/monthly-goal`).then(res => setMonthlyGoal(res.data?.monthly_target || null)).catch(() => {});
    loadHeatmap();
    loadAnnualPerformance();
  }, []);

  // Enrich monthly_sales with M2: if the "Monthly Sales" file itself doesn't have a real
  // m² per month, distribute the known YTD total M2 (from the Hit Parade Excel) across months
  // proportionally to each month's share of sales, so the monthly M2 list/charts always show
  // a number instead of "no data".
  const enrichedMonthlySales = useMemo(() => {
    if (!vendasData?.monthly_sales) return [];
    const hasRealM2 = vendasData.monthly_sales.some(m => m.m2 !== null && m.m2 !== undefined);
    if (hasRealM2) return vendasData.monthly_sales;
    const totalM2 = vendasData.totals?.total_m2 || 0;
    const totalSales = vendasData.monthly_sales.reduce((sum, m) => sum + (m.sales || 0), 0);
    if (totalM2 <= 0 || totalSales <= 0) return vendasData.monthly_sales;
    return vendasData.monthly_sales.map(m => ({
      ...m,
      m2: Math.round((m.sales / totalSales) * totalM2 * 100) / 100,
      m2Estimated: true
    }));
  }, [vendasData]);

  // Filter monthly data by selected period
  const filteredMonthlyData = useMemo(() => {
    if (!enrichedMonthlySales.length) return [];
    if (selectedPeriod === 'all') return enrichedMonthlySales;
    
    return enrichedMonthlySales.filter(m => 
      MONTH_TO_QUARTER[m.month] === selectedPeriod
    );
  }, [enrichedMonthlySales, selectedPeriod]);

  // Only some GPP "Monthly Sales" exports include a quantity/M2 column per month - the unified
  // chart below only shows the M2 line/axis when that data is actually present in the file.
  const hasMonthlyM2 = useMemo(() => filteredMonthlyData.some(m => m.m2 !== null && m.m2 !== undefined), [filteredMonthlyData]);
  const isMonthlyM2Estimated = useMemo(() => filteredMonthlyData.some(m => m.m2Estimated), [filteredMonthlyData]);
  // AVG PR / % 1° - valores reais do relatório GPP_Monthly_Sales (não estimados), usados como
  // "Preço Médio" (mais fiável que o cálculo vendas/m² quando o m² é estimado) e nova coluna
  // "% 1ª Escolha".
  const hasAvgPriceReported = useMemo(() => filteredMonthlyData.some(m => m.avg_price_reported != null), [filteredMonthlyData]);
  const hasPctFirstChoice = useMemo(() => filteredMonthlyData.some(m => m.pct_first_choice != null), [filteredMonthlyData]);

  // Calculate quarterly data for comparison chart
  const quarterlyData = useMemo(() => {
    if (!enrichedMonthlySales.length) return [];
    
    const quarters = {};
    enrichedMonthlySales.forEach(m => {
      const q = MONTH_TO_QUARTER[m.month];
      if (!quarters[q]) {
        quarters[q] = { quarter: q, sales: 0, m2: 0, hasM2: false, months: 0 };
      }
      quarters[q].sales += m.sales || 0;
      if (m.m2 !== null && m.m2 !== undefined) {
        quarters[q].m2 += m.m2;
        quarters[q].hasM2 = true;
      }
      quarters[q].months += 1;
    });
    
    return ['Q1', 'Q2', 'Q3', 'Q4']
      .filter(q => quarters[q])
      .map(q => quarters[q]);
  }, [enrichedMonthlySales]);
  const hasQuarterlyM2 = useMemo(() => quarterlyData.some(q => q.hasM2), [quarterlyData]);

  // Calculate filtered totals
  const filteredTotals = useMemo(() => {
    const data = filteredMonthlyData;
    const totalSales = data.reduce((sum, m) => sum + (m.sales || 0), 0);
    // Média ponderada pelas vendas de cada mês (não uma simples média aritmética), para o
    // Preço Médio/% 1ª Escolha da linha TOTAL refletirem corretamente o peso de cada mês.
    const withAvgPrice = data.filter(m => m.avg_price_reported != null && m.sales);
    const avgPriceReported = withAvgPrice.length
      ? withAvgPrice.reduce((sum, m) => sum + m.avg_price_reported * m.sales, 0) / withAvgPrice.reduce((sum, m) => sum + m.sales, 0)
      : null;
    const withPctFirstChoice = data.filter(m => m.pct_first_choice != null && m.sales);
    const pctFirstChoice = withPctFirstChoice.length
      ? withPctFirstChoice.reduce((sum, m) => sum + m.pct_first_choice * m.sales, 0) / withPctFirstChoice.reduce((sum, m) => sum + m.sales, 0)
      : null;
    return { totalSales, avgPriceReported, pctFirstChoice };
  }, [filteredMonthlyData]);

  // Previsão de Fecho: projeção linear de fecho do mês em curso, com base no "dia X de Y"
  // (dias úteis do mês) reportado no PDF Monthly Sales. Objetivo tem prioridade: 1º o
  // "Target END of MONTH" do PDF (quando existir), 2º o "Objetivo Mensal" manual definido em
  // Configurações; se nenhum dos dois existir, a métrica fica escondida (não inventamos meta).
  const previsaoFecho = useMemo(() => {
    const totals = vendasData?.totals;
    if (!totals) return null;
    const { current_month_day, current_month_total_days, current_month_sales } = totals;
    if (!current_month_day || !current_month_total_days || current_month_sales == null) return null;
    const target = totals.current_month_target_from_pdf || monthlyGoal;
    if (!target) return null;

    const dailyPace = current_month_sales / current_month_day;
    const projectedClose = dailyPace * current_month_total_days;
    const pctOfTarget = (projectedClose / target) * 100;
    const remainingDays = current_month_total_days - current_month_day;
    const remainingToTarget = Math.max(0, target - current_month_sales);
    const dailyDeficit = remainingDays > 0 ? remainingToTarget / remainingDays : null;

    return {
      projectedClose, pctOfTarget, target, remainingDays, remainingToTarget, dailyDeficit,
      currentMonthDay: current_month_day, currentMonthTotalDays: current_month_total_days,
      currentMonthSales: current_month_sales,
      targetSource: totals.current_month_target_from_pdf ? 'pdf' : 'manual',
      // Nos primeiros 5 dias úteis do mês a projeção linear (vendas/dia já feito × dias
      // totais) não é fiável - poucos dias de dados fazem o número oscilar muito e podem
      // parecer um défice alarmante sem motivo. Mostra-se uma mensagem neutra em vez disso.
      isEarlyMonth: current_month_day <= 5
    };
  }, [vendasData, monthlyGoal]);

  // Global product ranking, derived client-side from all customers' top_products
  const salesByProduct = useMemo(() => {
    if (!vendasData?.sales_by_customer) return [];
    const map = {};
    vendasData.sales_by_customer.forEach(c => {
      (c.top_products || []).forEach(p => {
        if (!p.material) return;
        if (!map[p.material]) map[p.material] = { material: p.material, sales: 0, qty: 0 };
        map[p.material].sales += p.sales || 0;
        map[p.material].qty += p.qty || 0;
      });
    });
    return Object.values(map).map(p => ({
      ...p,
      avgPrice: p.qty > 0 ? p.sales / p.qty : null
    })).sort((a, b) => b.sales - a.sales);
  }, [vendasData]);

  const sortedSalesByProduct = useMemo(
    () => sortRows(salesByProduct, productSort, (item, key) => {
      const fieldMap = { m2: 'qty', avg_price: 'avgPrice', sales: 'sales' };
      return item[fieldMap[key] || key];
    }),
    [salesByProduct, productSort]
  );

  // Vendas por Cor e Vendas por Medida - agregados a partir da mesma descrição/material dos
  // top_products de cada cliente (extractColor/extractSize acima), sem precisar de nenhum
  // campo novo no backend. % Peso usa o mesmo total oficial (Net Sales YTD) da restante página.
  const buildGroupedSales = (extractFn, labelKey) => {
    if (!vendasData?.sales_by_customer) return [];
    const totalSales = vendasData?.totals?.total_sales || 0;
    const map = {};
    vendasData.sales_by_customer.forEach(c => {
      (c.top_products || []).forEach(p => {
        const label = extractFn(p.material);
        if (!label) return;
        if (!map[label]) map[label] = { [labelKey]: label, sales: 0, qty: 0 };
        map[label].sales += p.sales || 0;
        map[label].qty += p.qty || 0;
      });
    });
    return Object.values(map).map(p => ({
      ...p,
      avgPrice: p.qty > 0 ? p.sales / p.qty : null,
      pct_of_total_sales: totalSales > 0 ? Math.round((p.sales / totalSales) * 1000) / 10 : null
    })).sort((a, b) => b.sales - a.sales);
  };
  const salesByColor = useMemo(() => buildGroupedSales(extractColor, 'color'), [vendasData]);
  const salesBySize = useMemo(() => buildGroupedSales(extractSize, 'size'), [vendasData]);
  const salesByTemperature = useMemo(() => buildGroupedSales(extractColorTemperature, 'temperature'), [vendasData]);

  // Filtro por Temperatura: lista exata de clientes/materiais dessa temperatura, aberta ao
  // clicar num dos 3 grupos (Quente/Frio/Neutro) na card acima.
  const temperatureDetailItems = useMemo(() => {
    if (!selectedTemperature || !vendasData?.sales_by_customer) return [];
    const rows = [];
    vendasData.sales_by_customer.forEach((c) => {
      (c.top_products || []).forEach((p, pIdx) => {
        if (extractColorTemperature(p.material) === selectedTemperature) {
          rows.push({ ...p, customerName: displayCustomerName(c), rowKey: `${c.customer_full || c.customer}-${pIdx}` });
        }
      });
    });
    return rows.sort((a, b) => (b.sales || 0) - (a.sales || 0));
  }, [selectedTemperature, vendasData]);
  const sortedSalesByColor = useMemo(
    () => sortRows(salesByColor, colorSort, (item, key) => {
      const fieldMap = { m2: 'qty', avg_price: 'avgPrice', sales: 'sales', pct_of_total_sales: 'pct_of_total_sales' };
      return item[fieldMap[key] || key];
    }),
    [salesByColor, colorSort]
  );
  const sortedSalesBySize = useMemo(
    () => sortRows(salesBySize, sizeSort, (item, key) => {
      const fieldMap = { m2: 'qty', avg_price: 'avgPrice', sales: 'sales', pct_of_total_sales: 'pct_of_total_sales' };
      return item[fieldMap[key] || key];
    }),
    [salesBySize, sizeSort]
  );

  // Calculate total YoY from collections
  const calculateTotalYoY = () => {
    if (!vendasData?.sales_by_collection) return null;
    
    let totalCurrent = 0;
    let totalPrev = 0;
    
    for (const c of vendasData.sales_by_collection) {
      totalCurrent += c.sales || 0;
      totalPrev += c.prev_sales || 0;
    }
    
    if (totalPrev > 0) {
      return Math.round(((totalCurrent - totalPrev) / totalPrev) * 1000) / 10;
    }
    return null;
  };

  // Format last updated date
  const formatLastUpdated = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Download VENDAS PDF
  const downloadVendasPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/sales/vendas/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_vendas.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF descarregado com sucesso!");
    } catch (error) {
      console.error("PDF download error:", error);
      toast.error("Erro ao descarregar PDF: " + getApiErrorMessage(error));
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Open chatbot for AI analysis
  const openAIAnalysis = () => {
    window.dispatchEvent(new CustomEvent('openChatbot'));
    toast.info("Use o Assistente IA para fazer perguntas sobre as vendas");
  };

  const totalYoY = calculateTotalYoY();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <PageHeader
        icon={ChartLineUp}
        title="Análise de Vendas"
        subtitle="Visualize suas vendas, volume (m²) e preços médios em relação ao ano anterior"
        actions={vendasData && (
          <Button
            onClick={openAIAnalysis}
            size="sm"
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
          >
            <Robot size={18} className="mr-1" />
            Analisar com IA
          </Button>
        )}
      />

      {/* Last Updated Banner with PDF Download */}
      {vendasData?.last_updated && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <Clock size={16} className="text-blue-600" />
            <span className="text-blue-700">
              Última atualización: <strong>{formatLastUpdated(vendasData.last_updated)}</strong>
            </span>
          </div>
          <Button
            onClick={downloadVendasPdf}
            disabled={downloadingPdf}
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {downloadingPdf ? (
              <LoadingSpinner size={16} className="mr-1" />
            ) : (
              <DownloadSimple size={16} className="mr-1" />
            )}
            PDF
          </Button>
        </div>
      )}

      {/* Period Filter */}
      {vendasData && vendasData.monthly_sales?.length > 0 && (
        <div className="gresart-card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <Funnel size={20} className="text-[#009392]" />
              <span className="font-medium text-stone-700">Filtrar por Período</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'all', label: 'Todo o Ano' },
                { value: 'Q1', label: 'Q1 (Jan-Mar)' },
                { value: 'Q2', label: 'Q2 (Abr-Jun)' },
                { value: 'Q3', label: 'Q3 (Jul-Set)' },
                { value: 'Q4', label: 'Q4 (Out-Dez)' }
              ].map(period => (
                <button
                  key={period.value}
                  data-testid={`period-filter-${period.value}`}
                  onClick={() => setSelectedPeriod(period.value)}
                  className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-all ${
                    selectedPeriod === period.value
                      ? 'bg-[#009392] text-white'
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  {period.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Filtered Totals */}
          {selectedPeriod !== 'all' && (
            <div className="mt-4 pt-4 border-t border-stone-200 text-center">
              <p className="text-sm text-stone-500">Vendas {selectedPeriod}</p>
              <p className="text-xl font-bold text-[#009392]">{formatCurrency(filteredTotals.totalSales)}</p>
            </div>
          )}
        </div>
      )}

      {/* Upload moved to Configurações */}
      {!vendasData && (
        <div className="gresart-card text-center py-6">
          <ChartLineUp size={40} className="mx-auto text-[#009392] mb-2" />
          <p className="text-stone-600 font-medium">Ainda não há dados de vendas carregados</p>
          <p className="text-stone-400 text-sm mt-1 mb-4">Carregue os ficheiros GPP em Configurações → Dados de Vendas (GPP)</p>
          <Link to="/settings" data-testid="link-settings-sales-upload">
            <Button className="bg-gradient-to-r from-[#009392] to-[#00b3b0] hover:from-[#007a7a] hover:to-[#009392]">
              <Gear size={18} className="mr-2" />
              Ir para Configurações
            </Button>
          </Link>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="gresart-card text-center py-8">
          <LoadingSpinner size={40} className="mx-auto mb-2" />
          <p className="text-stone-500">Carregando dados...</p>
        </div>
      )}

      {/* VENDAS Results */}
      {!loading && vendasData && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <div className="gresart-card relative bg-gradient-to-br from-[#009392]/10 to-transparent border-[#009392]/30 flex flex-col">
              <div className="absolute bottom-4 right-4 p-3 bg-[#009392]/20 rounded-full">
                <CurrencyEur size={28} className="text-[#009392]" />
              </div>
              <div>
                <p className="text-sm text-stone-500 font-medium">Total Vendas</p>
                <p className="text-2xl font-bold text-[#009392]" data-testid="total-sales">
                  {formatCurrency(vendasData.totals?.total_sales || 0)}
                </p>
                {totalYoY !== null && (
                  <div className="mt-1">
                    <YoYBadge value={totalYoY} size="lg" />
                  </div>
                )}
              </div>
              <p className="text-xs text-stone-400 mt-auto pt-2 pr-14">{vendasData.totals?.period || 'YTD'}</p>
            </div>

            <div className="gresart-card relative bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/30 flex flex-col">
              <div className="absolute bottom-4 right-4 p-3 bg-amber-500/20 rounded-full">
                <Clock size={28} className="text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500 font-medium">Pendente</p>
                <p className="text-2xl font-bold text-amber-600" data-testid="total-pending">
                  {formatCurrency(vendasData.pedidos_pendentes?.total_value || 0)}
                </p>
              </div>
              <p className="text-xs text-stone-400 mt-auto pt-2 pr-14">
                {(vendasData.pedidos_pendentes?.total_m2 || 0).toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m² por faturar
              </p>
            </div>

            <div className="gresart-card relative bg-gradient-to-br from-teal-500/10 to-transparent border-teal-500/30 flex flex-col">
              <div className="absolute bottom-4 right-4 p-3 bg-teal-500/20 rounded-full">
                <Cube size={28} className="text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500 font-medium">M2 Vendido</p>
                <p className="text-2xl font-bold text-teal-600" data-testid="total-m2">
                  {(vendasData.totals?.total_m2 || 0).toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²
                </p>
                {vendasData.totals?.yoy_m2_change != null && (
                  <div className="mt-1">
                    <YoYBadge value={vendasData.totals.yoy_m2_change} />
                  </div>
                )}
              </div>
              <p className="text-xs text-stone-400 mt-auto pt-2 pr-14">Volume total vendido</p>
            </div>

            <div className="gresart-card relative bg-gradient-to-br from-sky-500/10 to-transparent border-sky-500/30 flex flex-col">
              <div className="absolute bottom-4 right-4 p-3 bg-sky-500/20 rounded-full">
                <CurrencyEur size={28} className="text-sky-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500 font-medium">Preço Médio</p>
                <p className="text-2xl font-bold text-sky-600" data-testid="avg-price">
                  {vendasData.totals?.avg_price != null ? `${formatCurrency(vendasData.totals.avg_price)}/m²` : '—'}
                </p>
              </div>
              <p className="text-xs text-stone-400 mt-auto pt-2 pr-14">Vendas ÷ m²</p>
            </div>

            <div className="gresart-card relative bg-gradient-to-br from-blue-500/10 to-transparent border-blue-500/30 flex flex-col">
              <div className="absolute bottom-4 right-4 p-3 bg-blue-500/20 rounded-full">
                <Tag size={28} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500 font-medium">Séries</p>
                <p className="text-2xl font-bold text-blue-600">
                  {vendasData.sales_by_collection?.length || 0}
                </p>
              </div>
              <p className="text-xs text-stone-400 mt-auto pt-2 pr-14">Com vendas no período</p>
            </div>

            <div className="gresart-card relative bg-gradient-to-br from-purple-500/10 to-transparent border-purple-500/30 flex flex-col">
              <div className="absolute bottom-4 right-4 p-3 bg-purple-500/20 rounded-full">
                <Buildings size={28} className="text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500 font-medium">Clientes</p>
                <p className="text-2xl font-bold text-purple-600">
                  {vendasData.sales_by_customer?.length || 0}
                </p>
              </div>
              <p className="text-xs text-stone-400 mt-auto pt-2 pr-14">Com vendas no período</p>
            </div>
          </div>

          {/* Single-Client Search (full width) */}
          <div className="gresart-card relative">
            <h3 className="text-sm font-semibold text-stone-700 mb-2 flex items-center gap-2">
              <MagnifyingGlass size={18} className="text-[#009392]" />
              Pesquisar estatísticas de 1 cliente
            </h3>
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                value={clientSearchQuery}
                onChange={(e) => {
                  setClientSearchQuery(e.target.value);
                  setShowClientDropdown(true);
                }}
                onFocus={() => setShowClientDropdown(true)}
                onBlur={() => setTimeout(() => setShowClientDropdown(false), 150)}
                placeholder="Escreva o nome do cliente..."
                className="pl-9"
                data-testid="client-search-input"
              />
              {showClientDropdown && clientSearchResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="client-search-results">
                  {clientSearchResults.map((c, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setSelectedClientDetail(c);
                        setClientSearchQuery('');
                        setShowClientDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#009392]/10 border-b border-stone-100 last:border-b-0 flex items-center justify-between gap-2"
                      data-testid={`client-search-result-${idx}`}
                    >
                      <span className="truncate text-stone-700">{displayCustomerName(c)}</span>
                      <span className="text-[#009392] font-semibold text-xs whitespace-nowrap">{formatCurrency(c.sales)}</span>
                    </button>
                  ))}
                </div>
              )}
              {showClientDropdown && clientSearchQuery.trim() && clientSearchResults.length === 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg px-3 py-2 text-sm text-stone-400" data-testid="no-client-results">
                  Nenhum cliente encontrado
                </div>
              )}
            </div>
          </div>

          {/* Rappel 2026 + Comissões - side by side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link
              to="/rappel"
              data-testid="btn-rappel-sales-analysis"
              className="gresart-card flex items-center justify-center gap-3 bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/30 hover:from-amber-500/20 transition-colors"
            >
              <div className="p-3 bg-amber-500/20 rounded-full">
                <Percent size={24} className="text-amber-600" />
              </div>
              <span className="text-lg font-bold text-amber-700">Rappel 2026</span>
            </Link>

            <Link
              to="/comissoes"
              data-testid="btn-comissoes-sales-analysis"
              className="gresart-card flex items-center justify-center gap-3 bg-gradient-to-br from-emerald-500/10 to-transparent border-emerald-500/30 hover:from-emerald-500/20 transition-colors"
            >
              <div className="p-3 bg-emerald-500/20 rounded-full">
                <CurrencyEur size={24} className="text-emerald-600" />
              </div>
              <span className="text-lg font-bold text-emerald-700">Comissões</span>
            </Link>
          </div>

          {/* Single-Client Detail Dialog */}
          <Dialog open={!!selectedClientDetail} onOpenChange={(open) => !open && setSelectedClientDetail(null)}>
            <DialogContent className="max-w-2xl w-[92vw] sm:w-full max-h-[85vh] overflow-y-auto" data-testid="client-detail-dialog">
              {selectedClientDetail && (
                <>
                  <DialogHeader>
                    <DialogTitle className="text-stone-800 pr-8 break-words">{displayCustomerName(selectedClientDetail)}</DialogTitle>
                    <DialogDescription>
                      Estatísticas completas de vendas deste cliente: total de vendas, m² vendidos e todos os produtos comprados.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
                    <div className="p-3 rounded-xl bg-[#009392]/10 border border-[#009392]/20">
                      <p className="text-xs text-stone-500">Total Vendas</p>
                      <p className="text-lg font-bold text-[#009392]" data-testid="client-detail-sales">{formatCurrency(selectedClientDetail.sales)}</p>
                      {selectedClientDetail.yoy_change !== null && selectedClientDetail.yoy_change !== undefined && (
                        <div className="mt-1"><YoYBadge value={selectedClientDetail.yoy_change} /></div>
                      )}
                    </div>
                    <div className="p-3 rounded-xl bg-teal-500/10 border border-teal-500/20">
                      <p className="text-xs text-stone-500">M2 Vendido</p>
                      <p className="text-lg font-bold text-teal-600">{selectedClientM2.m2.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²</p>
                    </div>
                    <div className="p-3 rounded-xl bg-sky-500/10 border border-sky-500/20">
                      <p className="text-xs text-stone-500">Preço Médio</p>
                      <p className="text-lg font-bold text-sky-600">{selectedClientM2.avgPrice != null ? `${formatCurrency(selectedClientM2.avgPrice)}/m²` : '—'}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-purple-500/10 border border-purple-500/20">
                      <p className="text-xs text-stone-500">Produtos comprados</p>
                      <p className="text-lg font-bold text-purple-600">{selectedClientDetail.top_products?.length || 0}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Todos os produtos comprados</p>
                    <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1" data-testid="client-detail-products-list">
                      {(selectedClientDetail.top_products || []).length === 0 && (
                        <p className="text-sm text-stone-400">Sem dados de produtos para este cliente.</p>
                      )}
                      {(selectedClientDetail.top_products || []).map((p, pIdx) => (
                        <ProductRow key={pIdx} product={p} testId={`client-detail-product-${pIdx}`} />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>

          {/* Vendas por Cliente: Gráfico + Lista de todos os clientes */}
          {vendasData.sales_by_customer && vendasData.sales_by_customer.length > 0 && (
            <div className="gresart-card">
              <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <Buildings size={20} className="text-[#009392]" />
                Vendas por Cliente (Top 10)
              </h3>
              <div className="h-[500px] min-h-[500px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={vendasData.sales_by_customer.slice(0, 10).map(c => ({
                      ...c,
                      customer_full: displayCustomerName(c),
                      shortName: truncateText(displayCustomerName(c), 10)
                    }))} 
                    layout="vertical"
                    margin={{ top: 10, right: 30, left: 100, bottom: 10 }}
                    barCategoryGap="30%"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      type="number" 
                      tickFormatter={formatShortCurrency}
                      tick={{ fill: '#6b7280', fontSize: 11 }}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="shortName" 
                      tick={{ fill: '#374151', fontSize: 9 }}
                      width={95}
                      interval={0}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="sales" stackId="total" fill="#00b3b0" radius={[4, 0, 0, 4]} name="Vendas" />
                    <Bar dataKey="pending_value" stackId="total" fill="#00b3b0" fillOpacity={0.35} radius={[0, 4, 4, 0]} name="Pendentes">
                      <LabelList
                        dataKey="pending_value"
                        position="right"
                        content={({ x, y, width, height, index }) => {
                          const item = vendasData.sales_by_customer.slice(0, 10)[index];
                          if (!item) return null;
                          const total = (item.sales || 0) + (item.pending_value || 0);
                          return (
                            <text x={x + width + 8} y={y + height / 2} dy={4} fontSize={11} fontWeight={600} fill="#374151">
                              {formatShortCurrency(total)}
                            </text>
                          );
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-stone-400 mt-2 flex items-center gap-3">
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#00b3b0] inline-block"></span> Vendas</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#00b3b0] opacity-35 inline-block"></span> Pendentes</span>
                <span>— o número no final da barra é o potencial total (vendas + pendentes)</span>
              </p>

              {/* Full Customer Table */}
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-stone-700 mb-2">Todos os Clientes ({vendasData.sales_by_customer.length})</h4>
                <div className="max-h-96 overflow-auto border border-stone-200 rounded-lg">
                  <table className="w-full text-xs sm:text-sm sm:min-w-[480px]">
                    <thead className="sticky top-0 bg-stone-100">
                      <tr className="border-b border-stone-200">
                        <th className="text-left py-2 px-1.5 sm:px-3 font-medium text-stone-600">#</th>
                        <th className="text-left py-2 px-1.5 sm:px-3 font-medium text-stone-600">Cliente</th>
                        <SortableTh label="M2" sortKey="m2" sortState={clientSort} onSort={toggleClientSort} colorClass="text-teal-600" testId="th-sort-m2-cliente" />
                        <SortableTh label="Preço Médio" sortKey="avg_price" sortState={clientSort} onSort={toggleClientSort} colorClass="text-sky-600" testId="th-sort-preco-cliente" />
                        <SortableTh label="Vendas" sortKey="sales" sortState={clientSort} onSort={toggleClientSort} colorClass="text-stone-600" testId="th-sort-vendas-cliente" />
                        <SortableTh label="% Peso" sortKey="pct_of_total_sales" sortState={clientSort} onSort={toggleClientSort} colorClass="text-[#009392]" testId="th-sort-pct-peso-cliente" />
                        <th className="text-right py-2 px-1.5 sm:px-3 font-medium text-amber-600">Pendentes</th>
                        <SortableTh label="YoY" sortKey="yoy_change" sortState={clientSort} onSort={toggleClientSort} colorClass="text-stone-600" testId="th-sort-yoy-cliente" />
                        <th className="text-right py-2 px-1.5 sm:px-3 font-medium text-stone-600">
                          <UiTooltipProvider delayDuration={150}>
                            <UiTooltip>
                              <UiTooltipTrigger asChild>
                                <span className="inline-flex items-center gap-1 cursor-help" data-testid="sinais-legend-trigger">
                                  Sinais
                                  <Question size={13} weight="bold" className="text-stone-400" />
                                </span>
                              </UiTooltipTrigger>
                              <UiTooltipContent side="left" className="bg-white text-stone-700 border border-stone-200 shadow-lg px-3 py-2.5" data-testid="sinais-legend-tooltip">
                                <SinaisLegendContent />
                              </UiTooltipContent>
                            </UiTooltip>
                          </UiTooltipProvider>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedSalesByCustomer.map((item, idx) => {
                        const customerKey = item.customer_full || item.customer;
                        const expandState = expandedCustomerProducts[customerKey] || 'closed';
                        const hasProducts = item.top_products && item.top_products.length > 0;
                        const visibleProducts = expandState === 'all'
                          ? (item.top_products || [])
                          : (item.top_products || []).slice(0, 3);
                        return (
                          <Fragment key={idx}>
                            <tr className="border-b border-stone-100 hover:bg-stone-50">
                              <td className="py-2 px-1.5 sm:px-3 text-stone-400">{idx + 1}</td>
                              <td className="py-2 px-1.5 sm:px-3 font-medium text-stone-700 max-w-[30vw] sm:max-w-none truncate" title={item.customer_full || item.customer}>
                                <div className="flex items-center gap-1.5">
                                  {hasProducts && (
                                    <button
                                      onClick={() => toggleCustomerProducts(customerKey)}
                                      className="text-stone-400 hover:text-[#009392] transition-colors flex-shrink-0"
                                      data-testid={`toggle-customer-products-${idx}`}
                                    >
                                      {expandState !== 'closed' ? <CaretDown size={14} /> : <CaretRight size={14} />}
                                    </button>
                                  )}
                                  <span className="truncate">{displayCustomerName(item)}</span>
                                </div>
                              </td>
                              <td className="py-2 px-1.5 sm:px-3 text-right text-teal-600 whitespace-nowrap">
                                {item.m2 != null ? `${item.m2.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²` : '—'}
                              </td>
                              <td className="py-2 px-1.5 sm:px-3 text-right text-sky-600 whitespace-nowrap">
                                {item.avg_price != null ? `${formatCurrency(item.avg_price)}/m²` : '—'}
                              </td>
                              <td className="py-2 px-1.5 sm:px-3 text-right text-stone-600 whitespace-nowrap">{formatCurrency(item.sales)}</td>
                              <td className="py-2 px-1.5 sm:px-3 text-right text-[#009392] font-medium whitespace-nowrap" data-testid={`pct-peso-cliente-${idx}`}>
                                {item.pct_of_total_sales != null ? `${item.pct_of_total_sales}%` : '—'}
                              </td>
                              <td className="py-2 px-1.5 sm:px-3 text-right text-amber-600 whitespace-nowrap" data-testid={`pending-value-${idx}`}>
                                {item.pending_value > 0 ? formatCurrency(item.pending_value) : '—'}
                              </td>
                              <td className="py-2 px-1.5 sm:px-3 text-right">
                                <YoYBadge value={item.yoy_change} />
                              </td>
                              <td className="py-2 px-1.5 sm:px-3 text-right">
                                <IndicadoresCell item={item} testId={`indicadores-cliente-${idx}`} />
                              </td>
                            </tr>
                            {hasProducts && expandState !== 'closed' && (
                              <tr className="bg-[#009392]/5">
                                <td></td>
                                <td colSpan={8} className="py-2 px-3">
                                  <div className="space-y-1.5" data-testid={`customer-products-list-${idx}`}>
                                    <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                                      {expandState === 'all'
                                        ? `Todas as referências compradas (${item.top_products.length})`
                                        : 'Top 3 produtos mais comprados'}
                                    </p>
                                    <div className={expandState === 'all' ? 'space-y-1.5 max-h-80 overflow-y-auto pr-1' : 'space-y-1.5'}>
                                      {visibleProducts.map((p, pIdx) => (
                                        <ProductRow key={pIdx} product={p} testId={`customer-${idx}-product-${pIdx}`} />
                                      ))}
                                    </div>
                                    {expandState === 'top3' && item.top_products.length > 3 && (
                                      <button
                                        onClick={() => showAllProducts(customerKey)}
                                        className="text-xs text-[#009392] font-medium hover:underline pt-1"
                                        data-testid={`show-top10-products-${idx}`}
                                      >
                                        Ver todas as {item.top_products.length} referências compradas →
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Matriz de Saúde da Carteira (Quadrante Mágico): bubble chart estratégico -
              X=Volume (m²), Y=Preço Médio (€/m²), tamanho da bolha=Valor Faturado (€) */}
          {bubbleMatrixStats && (
            <div className="gresart-card" data-testid="bubble-matrix-section">
              <h3 className="text-lg font-semibold text-stone-800 mb-1 flex items-center gap-2">
                <Target size={20} className="text-[#009392]" />
                Matriz de Saúde da Carteira (Quadrante Mágico)
              </h3>
              <p className="text-xs text-stone-400 mb-4">
                Cada bolha é um cliente — eixo horizontal: Volume (m²) · eixo vertical: Preço Médio (€/m²) · tamanho da bolha: Valor Faturado (€).
                {" "}<span className="hidden sm:inline">Toque ou passe o cursor numa bolha para ver os valores exatos.</span>
              </p>

              <div className="h-[420px] sm:h-[460px]" data-testid="bubble-matrix-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      type="number" dataKey="m2" name="Volume"
                      domain={[0, bubbleMatrixStats.maxM2]}
                      tickFormatter={(v) => `${(v / 1000).toFixed(0)}k m²`}
                      tick={{ fill: '#6b7280', fontSize: 11 }}
                      label={{ value: 'Volume (m²)', position: 'insideBottom', offset: -6, fill: '#78716c', fontSize: 12 }}
                    />
                    <YAxis
                      type="number" dataKey="avg_price" name="Preço Médio"
                      domain={[0, bubbleMatrixStats.maxPrice]}
                      tickFormatter={(v) => `${v.toFixed(0)}€`}
                      tick={{ fill: '#6b7280', fontSize: 11 }}
                      label={{ value: 'Preço Médio (€/m²)', angle: -90, position: 'insideLeft', fill: '#78716c', fontSize: 12 }}
                    />
                    <ZAxis type="number" dataKey="sales" range={[80, 1800]} name="Valor Faturado" />
                    <Tooltip content={<BubbleMatrixTooltip />} cursor={{ strokeDasharray: '3 3' }} />

                    {/* 4 quadrantes com fundo tênue + etiqueta - divididos pela mediana real da carteira */}
                    <ReferenceArea x1={bubbleMatrixStats.medianM2} x2={bubbleMatrixStats.maxM2} y1={bubbleMatrixStats.medianPrice} y2={bubbleMatrixStats.maxPrice} fill="#f59e0b" fillOpacity={0.08} label={{ value: 'Estrelas', position: 'insideTopRight', fill: '#b45309', fontSize: 12, fontWeight: 600 }} />
                    <ReferenceArea x1={bubbleMatrixStats.medianM2} x2={bubbleMatrixStats.maxM2} y1={0} y2={bubbleMatrixStats.medianPrice} fill="#009392" fillOpacity={0.06} label={{ value: 'Volume', position: 'insideBottomRight', fill: '#00726f', fontSize: 12, fontWeight: 600 }} />
                    <ReferenceArea x1={0} x2={bubbleMatrixStats.medianM2} y1={bubbleMatrixStats.medianPrice} y2={bubbleMatrixStats.maxPrice} fill="#a855f7" fillOpacity={0.08} label={{ value: 'Joias', position: 'insideTopLeft', fill: '#7e22ce', fontSize: 12, fontWeight: 600 }} />
                    <ReferenceArea x1={0} x2={bubbleMatrixStats.medianM2} y1={0} y2={bubbleMatrixStats.medianPrice} fill="#78716c" fillOpacity={0.08} label={{ value: 'Drenagem', position: 'insideBottomLeft', fill: '#57534e', fontSize: 12, fontWeight: 600 }} />
                    <ReferenceLine x={bubbleMatrixStats.medianM2} stroke="#d6d3d1" strokeDasharray="4 4" />
                    <ReferenceLine y={bubbleMatrixStats.medianPrice} stroke="#d6d3d1" strokeDasharray="4 4" />

                    <Scatter
                      data={bubbleMatrixData}
                      onClick={(point) => setSelectedBubble(point)}
                      cursor="pointer"
                      data-testid="bubble-matrix-scatter"
                    >
                      {bubbleMatrixData.map((d, i) => {
                        const quadrant = bubbleQuadrant(d, bubbleMatrixStats);
                        const color = { Estrelas: '#d97706', Volume: '#009392', Joias: '#a855f7', Drenagem: '#a8a29e' }[quadrant];
                        const isDimmed = activeQuadrant && activeQuadrant !== quadrant;
                        return <Cell key={i} fill={color} fillOpacity={isDimmed ? 0.08 : 0.75} stroke={color} strokeOpacity={isDimmed ? 0.15 : 1} strokeWidth={1} />;
                      })}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>

              {/* Ficha do cliente tocado/selecionado - garante que funciona ao toque no iPad,
                  não só ao passar o cursor (a Tooltip nativa do Recharts fica só no hover) */}
              {selectedBubble && (
                <div className="mt-3 rounded-lg border border-[#009392]/30 bg-[#009392]/5 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="bubble-matrix-selected-client">
                  <div>
                    <p className="font-semibold text-stone-800 text-sm">{selectedBubble.customer_full}</p>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Volume: <span className="font-medium text-teal-700">{selectedBubble.m2.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²</span>
                      {" · "}Preço Médio: <span className="font-medium text-sky-700">{formatCurrency(selectedBubble.avg_price)}/m²</span>
                      {" · "}Faturado: <span className="font-medium text-stone-700">{formatCurrency(selectedBubble.sales)}</span>
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedBubble(null)}
                    className="text-xs text-stone-400 hover:text-stone-600 transition-colors"
                    data-testid="bubble-matrix-clear-selection"
                  >
                    Fechar
                  </button>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[11px] mt-3 pt-3 border-t border-stone-100" data-testid="bubble-matrix-quadrant-filters">
                {[
                  { name: 'Estrelas', icon: <Star size={12} weight="fill" className="text-amber-600" />, desc: 'alto volume, alto preço', activeClass: 'bg-amber-50 border-amber-300 text-amber-700' },
                  { name: 'Volume', icon: <span className="w-2.5 h-2.5 rounded-full inline-block bg-[#009392]" />, desc: 'alto volume, preço baixo', activeClass: 'bg-teal-50 border-teal-300 text-teal-700' },
                  { name: 'Joias', icon: <Diamond size={12} weight="fill" className="text-purple-500" />, desc: 'pouco volume, preço alto', activeClass: 'bg-purple-50 border-purple-300 text-purple-700' },
                  { name: 'Drenagem', icon: <span className="w-2.5 h-2.5 rounded-full inline-block bg-stone-400" />, desc: 'baixo volume, preço baixo', activeClass: 'bg-stone-100 border-stone-400 text-stone-700' },
                ].map((q) => (
                  <button
                    key={q.name}
                    onClick={() => setActiveQuadrant(activeQuadrant === q.name ? null : q.name)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 transition-colors ${
                      activeQuadrant === q.name ? q.activeClass : 'border-transparent text-stone-500 hover:bg-stone-50'
                    }`}
                    data-testid={`bubble-matrix-quadrant-filter-${q.name.toLowerCase()}`}
                  >
                    {q.icon} <span className="font-medium">{q.name}</span> — {q.desc}
                  </button>
                ))}
                {activeQuadrant && (
                  <button
                    onClick={() => setActiveQuadrant(null)}
                    className="text-stone-400 hover:text-stone-600 px-1"
                    data-testid="bubble-matrix-quadrant-filter-clear"
                  >
                    Limpar filtro
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Gráficos: Vendas por Produto + Vendas por Série, lado a lado */}
          {(salesByProduct.length > 0 || (vendasData.sales_by_collection && vendasData.sales_by_collection.length > 0)) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="charts-produto-serie-row">
              {salesByProduct.length > 0 ? (
                <div className="gresart-card">
                  <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                    <Cube size={20} className="text-[#009392]" />
                    Vendas por Produto
                  </h3>
                  <div className="h-96 min-h-[384px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={salesByProduct.slice(0, 10).map(p => ({
                          ...p,
                          shortName: cleanProductName(p.material)
                        }))} 
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tickFormatter={formatShortCurrency} tick={{ fill: '#6b7280', fontSize: 11 }} />
                        <YAxis type="category" dataKey="shortName" tick={<WrappedYAxisTick />} width={110} interval={0} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="sales" fill="#00b3b0" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                // Placeholder que mantém o layout de 2 colunas mesmo quando o ficheiro
                // carregado (ex: PDF de fallback) não trouxe detalhe por produto/top_products.
                <div className="gresart-card flex flex-col items-center justify-center text-center h-96 min-h-[384px]" data-testid="vendas-produto-empty">
                  <Cube size={28} className="text-stone-300 mb-2" />
                  <p className="text-sm text-stone-400">Sem dados por produto neste carregamento.</p>
                </div>
              )}

              {vendasData.sales_by_collection && vendasData.sales_by_collection.length > 0 && (
                <div className="gresart-card">
                  <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                    <Tag size={20} className="text-[#009392]" />
                    Vendas por Série
                  </h3>
                  <div className="h-96 min-h-[384px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={vendasData.sales_by_collection.slice(0, 10).map(c => ({
                          ...c,
                          shortName: toSentenceCase(c.collection)
                        }))} 
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tickFormatter={formatShortCurrency} tick={{ fill: '#6b7280', fontSize: 11 }} />
                        <YAxis type="category" dataKey="shortName" tick={<WrappedYAxisTick />} width={110} interval={0} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="sales" fill="#009392" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Listas: Vendas de Produtos, depois Vendas por Série - em coluna única e largura
              total, para que todas as colunas (% Peso, Vendas, M2, etc.) fiquem visíveis
              sem scroll lateral. */}
          {(salesByProduct.length > 0 || (vendasData.sales_by_collection && vendasData.sales_by_collection.length > 0)) && (
            <div className="space-y-6" data-testid="lists-produto-serie-row">
              {salesByProduct.length > 0 ? (
                <div className="gresart-card">
                  <h4 className="text-sm font-semibold text-stone-700 mb-2">Todos os Produtos ({salesByProduct.length})</h4>
                  <div className="max-h-96 overflow-y-auto border border-stone-200 rounded-lg">
                    <table className="w-full text-xs sm:text-sm">
                      <thead className="sticky top-0 bg-stone-100">
                        <tr className="border-b border-stone-200">
                          <th className="text-left py-2 px-2 sm:px-3 font-medium text-stone-600">#</th>
                          <th className="text-left py-2 px-2 sm:px-3 font-medium text-stone-600">Produto</th>
                          <SortableTh label="M2" sortKey="m2" sortState={productSort} onSort={toggleProductSort} colorClass="text-teal-600" testId="th-sort-m2-produto" />
                          <SortableTh label="Preço Médio" sortKey="avg_price" sortState={productSort} onSort={toggleProductSort} colorClass="text-sky-600" testId="th-sort-preco-produto" />
                          <SortableTh label="Vendas" sortKey="sales" sortState={productSort} onSort={toggleProductSort} colorClass="text-stone-600" testId="th-sort-vendas-produto" />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSalesByProduct.map((item, idx) => (
                          <tr key={idx} className="border-b border-stone-100 hover:bg-stone-50">
                            <td className="py-2 px-2 sm:px-3 text-stone-400">{idx + 1}</td>
                            <td className="py-2 px-2 sm:px-3 font-medium text-stone-700 max-w-[28vw] sm:max-w-none truncate" title={item.material}>{cleanProductName(item.material)}</td>
                            <td className="py-2 px-2 sm:px-3 text-right text-teal-600 whitespace-nowrap">{(item.qty || 0).toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²</td>
                            <td className="py-2 px-2 sm:px-3 text-right text-sky-600 whitespace-nowrap">{item.avgPrice != null ? `${formatCurrency(item.avgPrice)}/m²` : '—'}</td>
                            <td className="py-2 px-2 sm:px-3 text-right text-stone-600 whitespace-nowrap">{formatCurrency(item.sales)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="gresart-card flex flex-col items-center justify-center text-center py-10" data-testid="lista-produto-empty">
                  <Cube size={28} className="text-stone-300 mb-2" />
                  <p className="text-sm text-stone-400">Sem dados por produto neste carregamento.</p>
                </div>
              )}

              {/* Pesquisa de Vendas por Série - mostra os clientes que compraram uma série
                  específica no ano selecionado, ordenados por volume (m2) decrescente */}
              <div className="gresart-card" data-testid="series-search-card">
                <h3 className="text-sm font-semibold text-stone-700 mb-2 flex items-center gap-2">
                  <Tag size={18} className="text-[#009392]" />
                  Pesquisar Vendas por Série
                </h3>
                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                  <div className="relative flex-1">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input
                      value={seriesSearchQuery}
                      onChange={(e) => setSeriesSearchQuery(e.target.value)}
                      placeholder="Escreva o nome da série (ex: Oak)..."
                      className="pl-9"
                      data-testid="series-search-input"
                    />
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0 flex-wrap" data-testid="series-search-year-filter">
                    {[{ label: 'Todos os Anos', value: 'all' }, { label: '2026', value: '2026' }, { label: '2025', value: '2025' }].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSeriesSearchYear(opt.value)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${seriesSearchYear === opt.value ? 'bg-[#009392] text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                        data-testid={`series-search-year-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {seriesSearchQuery.trim() && (
                  seriesSearchResults.length > 0 ? (
                    <div className="max-h-80 overflow-y-auto border border-stone-200 rounded-lg" data-testid="series-search-results">
                      <table className="w-full text-xs sm:text-sm">
                        <thead className="sticky top-0 bg-stone-100">
                          <tr className="border-b border-stone-200">
                            <th className="text-left py-2 px-2 sm:px-3 font-medium text-stone-600">#</th>
                            <th className="text-left py-2 px-2 sm:px-3 font-medium text-stone-600">Cliente</th>
                            <th className="text-right py-2 px-2 sm:px-3 font-medium text-teal-600">M2</th>
                            <th className="text-right py-2 px-2 sm:px-3 font-medium text-stone-600">Vendas</th>
                          </tr>
                        </thead>
                        <tbody>
                          {seriesSearchResults.map((r, idx) => (
                            <tr key={idx} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`series-search-result-${idx}`}>
                              <td className="py-2 px-2 sm:px-3 text-stone-400">{idx + 1}</td>
                              <td className="py-2 px-2 sm:px-3 font-medium text-stone-700 truncate max-w-[40vw] sm:max-w-none">{displayCustomerName(r)}</td>
                              <td className="py-2 px-2 sm:px-3 text-right text-teal-600 whitespace-nowrap">{r.m2.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²</td>
                              <td className="py-2 px-2 sm:px-3 text-right text-stone-600 whitespace-nowrap">{formatCurrency(r.sales)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-stone-400 text-center py-6" data-testid="series-search-no-results">Nenhum cliente encontrado para esta série no período selecionado.</p>
                  )
                )}
              </div>

              {vendasData.sales_by_collection && vendasData.sales_by_collection.length > 0 && (
                <div className="gresart-card">
                  <h4 className="text-sm font-semibold text-stone-700 mb-2">Todas as Séries ({vendasData.sales_by_collection.length})</h4>
                  <div className="max-h-96 overflow-y-auto border border-stone-200 rounded-lg">
                    <table className="w-full text-xs sm:text-sm">
                      <thead className="sticky top-0 bg-stone-100">
                        <tr className="border-b border-stone-200">
                          <th className="text-left py-2 px-1.5 sm:px-3 font-medium text-stone-600">#</th>
                          <th className="text-left py-2 px-1.5 sm:px-3 font-medium text-stone-600">Série</th>
                          <SortableTh label="M2" sortKey="m2" sortState={serieSort} onSort={toggleSerieSort} colorClass="text-teal-600" testId="th-sort-m2-serie" />
                          <SortableTh label="Preço Médio" sortKey="avg_price" sortState={serieSort} onSort={toggleSerieSort} colorClass="text-sky-600" testId="th-sort-preco-serie" />
                          <SortableTh label="Vendas" sortKey="sales" sortState={serieSort} onSort={toggleSerieSort} colorClass="text-stone-600" testId="th-sort-vendas-serie" />
                          <SortableTh label="% Peso" sortKey="pct_of_total_sales" sortState={serieSort} onSort={toggleSerieSort} colorClass="text-[#009392]" testId="th-sort-pct-peso-serie" />
                          <th className="text-right py-2 px-1.5 sm:px-3 font-medium text-amber-600">Pendentes</th>
                          <SortableTh label="YoY" sortKey="yoy_change" sortState={serieSort} onSort={toggleSerieSort} colorClass="text-stone-600" testId="th-sort-yoy-serie" />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSalesByCollection.map((item, idx) => (
                          <tr key={idx} className="border-b border-stone-100 hover:bg-stone-50">
                            <td className="py-2 px-1.5 sm:px-3 text-stone-400">{idx + 1}</td>
                            <td className="py-2 px-1.5 sm:px-3 font-medium text-stone-700 max-w-[30vw] sm:max-w-none truncate">{toSentenceCase(item.collection)}</td>
                            <td className="py-2 px-1.5 sm:px-3 text-right text-teal-600 whitespace-nowrap">
                              {item.m2 != null ? `${item.m2.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²` : '—'}
                            </td>
                            <td className="py-2 px-1.5 sm:px-3 text-right text-sky-600 whitespace-nowrap">
                              {item.avg_price != null ? `${formatCurrency(item.avg_price)}/m²` : '—'}
                            </td>
                            <td className="py-2 px-1.5 sm:px-3 text-right text-stone-600 whitespace-nowrap">{formatCurrency(item.sales)}</td>
                            <td className="py-2 px-1.5 sm:px-3 text-right text-[#009392] font-medium whitespace-nowrap" data-testid={`pct-peso-serie-${idx}`}>
                              {item.pct_of_total_sales != null ? `${item.pct_of_total_sales}%` : '—'}
                            </td>
                            <td className="py-2 px-1.5 sm:px-3 text-right text-amber-600 whitespace-nowrap">
                              {item.pending_value > 0 ? formatCurrency(item.pending_value) : '—'}
                            </td>
                            <td className="py-2 px-1.5 sm:px-3 text-right">
                              <YoYBadge value={item.yoy_change} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Vendas por Cor */}
              <div className="gresart-card">
                <h4 className="text-sm font-semibold text-stone-700 mb-2">Vendas por Cor {salesByColor.length > 0 ? `(${salesByColor.length})` : ''}</h4>
                {salesByColor.length === 0 ? (
                  <p className="text-sm text-stone-400 text-center py-6">Sem cor identificável nas descrições deste carregamento.</p>
                ) : (
                  <div className="max-h-96 overflow-y-auto border border-stone-200 rounded-lg">
                    <table className="w-full text-xs sm:text-sm">
                      <thead className="sticky top-0 bg-stone-100">
                        <tr className="border-b border-stone-200">
                          <th className="text-left py-2 px-1.5 sm:px-3 font-medium text-stone-600">#</th>
                          <th className="text-left py-2 px-1.5 sm:px-3 font-medium text-stone-600">Cor</th>
                          <SortableTh label="M2" sortKey="m2" sortState={colorSort} onSort={toggleColorSort} colorClass="text-teal-600" testId="th-sort-m2-cor" />
                          <SortableTh label="Preço Médio" sortKey="avg_price" sortState={colorSort} onSort={toggleColorSort} colorClass="text-sky-600" testId="th-sort-preco-cor" />
                          <SortableTh label="Vendas" sortKey="sales" sortState={colorSort} onSort={toggleColorSort} colorClass="text-stone-600" testId="th-sort-vendas-cor" />
                          <SortableTh label="% Peso" sortKey="pct_of_total_sales" sortState={colorSort} onSort={toggleColorSort} colorClass="text-[#009392]" testId="th-sort-pct-peso-cor" />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSalesByColor.map((item, idx) => (
                          <tr key={idx} className="border-b border-stone-100 hover:bg-stone-50">
                            <td className="py-2 px-1.5 sm:px-3 text-stone-400">{idx + 1}</td>
                            <td className="py-2 px-1.5 sm:px-3 font-medium text-stone-700">{item.color}</td>
                            <td className="py-2 px-1.5 sm:px-3 text-right text-teal-600 whitespace-nowrap">
                              {item.qty != null ? `${item.qty.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²` : '—'}
                            </td>
                            <td className="py-2 px-1.5 sm:px-3 text-right text-sky-600 whitespace-nowrap">
                              {item.avgPrice != null ? `${formatCurrency(item.avgPrice)}/m²` : '—'}
                            </td>
                            <td className="py-2 px-1.5 sm:px-3 text-right text-stone-600 whitespace-nowrap">{formatCurrency(item.sales)}</td>
                            <td className="py-2 px-1.5 sm:px-3 text-right text-[#009392] font-medium whitespace-nowrap" data-testid={`pct-peso-cor-${idx}`}>
                              {item.pct_of_total_sales != null ? `${item.pct_of_total_sales}%` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Vendas por Medida */}
              <div className="gresart-card">
                <h4 className="text-sm font-semibold text-stone-700 mb-2">Vendas por Medida {salesBySize.length > 0 ? `(${salesBySize.length})` : ''}</h4>
                {salesBySize.length === 0 ? (
                  <p className="text-sm text-stone-400 text-center py-6">Sem medida identificável nas descrições deste carregamento.</p>
                ) : (
                  <div className="max-h-96 overflow-y-auto border border-stone-200 rounded-lg">
                    <table className="w-full text-xs sm:text-sm">
                      <thead className="sticky top-0 bg-stone-100">
                        <tr className="border-b border-stone-200">
                          <th className="text-left py-2 px-1.5 sm:px-3 font-medium text-stone-600">#</th>
                          <th className="text-left py-2 px-1.5 sm:px-3 font-medium text-stone-600">Medida</th>
                          <SortableTh label="M2" sortKey="m2" sortState={sizeSort} onSort={toggleSizeSort} colorClass="text-teal-600" testId="th-sort-m2-medida" />
                          <SortableTh label="Preço Médio" sortKey="avg_price" sortState={sizeSort} onSort={toggleSizeSort} colorClass="text-sky-600" testId="th-sort-preco-medida" />
                          <SortableTh label="Vendas" sortKey="sales" sortState={sizeSort} onSort={toggleSizeSort} colorClass="text-stone-600" testId="th-sort-vendas-medida" />
                          <SortableTh label="% Peso" sortKey="pct_of_total_sales" sortState={sizeSort} onSort={toggleSizeSort} colorClass="text-[#009392]" testId="th-sort-pct-peso-medida" />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSalesBySize.map((item, idx) => (
                          <tr key={idx} className="border-b border-stone-100 hover:bg-stone-50">
                            <td className="py-2 px-1.5 sm:px-3 text-stone-400">{idx + 1}</td>
                            <td className="py-2 px-1.5 sm:px-3 font-medium text-stone-700">{item.size}</td>
                            <td className="py-2 px-1.5 sm:px-3 text-right text-teal-600 whitespace-nowrap">
                              {item.qty != null ? `${item.qty.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²` : '—'}
                            </td>
                            <td className="py-2 px-1.5 sm:px-3 text-right text-sky-600 whitespace-nowrap">
                              {item.avgPrice != null ? `${formatCurrency(item.avgPrice)}/m²` : '—'}
                            </td>
                            <td className="py-2 px-1.5 sm:px-3 text-right text-stone-600 whitespace-nowrap">{formatCurrency(item.sales)}</td>
                            <td className="py-2 px-1.5 sm:px-3 text-right text-[#009392] font-medium whitespace-nowrap" data-testid={`pct-peso-medida-${idx}`}>
                              {item.pct_of_total_sales != null ? `${item.pct_of_total_sales}%` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Vendas por Temperatura de Cor (Quente/Frio/Neutro) */}
              <div className="gresart-card" data-testid="vendas-temperatura-cor">
                <h4 className="text-sm font-semibold text-stone-700 mb-1">Vendas por Temperatura de Cor</h4>
                {salesByTemperature.length > 0 && (
                  <p className="text-xs text-stone-400 mb-3">Clique num grupo para ver os clientes e materiais exatos.</p>
                )}
                {salesByTemperature.length === 0 ? (
                  <p className="text-sm text-stone-400 text-center py-6">Sem cor identificável nas descrições deste carregamento.</p>
                ) : (
                  <div className="space-y-3">
                    {(() => {
                      const maxSales = Math.max(...salesByTemperature.map(t => t.sales), 1);
                      return salesByTemperature.map((item, idx) => {
                        const meta = TEMPERATURE_META[item.temperature] || TEMPERATURE_META.Neutro;
                        const widthPct = Math.max(4, Math.round((item.sales / maxSales) * 100));
                        return (
                          <div
                            key={idx}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedTemperature(item.temperature)}
                            onKeyDown={(e) => e.key === 'Enter' && setSelectedTemperature(item.temperature)}
                            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                            data-testid={`temperatura-cor-row-${item.temperature.toLowerCase()}`}
                          >
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0 w-20 text-center ${meta.badge}`}>
                              {item.temperature}
                            </span>
                            <div className="flex-1 h-6 bg-stone-100 rounded-full overflow-hidden relative">
                              <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{ width: `${widthPct}%`, backgroundColor: meta.color }}
                              />
                            </div>
                            <div className="text-right flex-shrink-0 w-36">
                              <p className="text-sm font-semibold text-stone-700 whitespace-nowrap">{formatCurrency(item.sales)}</p>
                              <p className="text-xs text-[#009392] font-medium" data-testid={`pct-peso-temperatura-${item.temperature.toLowerCase()}`}>
                                {item.pct_of_total_sales != null ? `${item.pct_of_total_sales}% do total` : '—'}
                              </p>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>

              {/* Tendência Sazonal - Quente vs Frio vs Neutro por trimestre. Construído
                  automaticamente a cada carregamento de vendas (snapshot leve, sem re-enviar
                  ficheiros antigos) - só aparece quando já há pelo menos 2 trimestres gravados. */}
              <div className="gresart-card" data-testid="vendas-tendencia-sazonal">
                <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
                  <h4 className="text-sm font-semibold text-stone-700">Tendência Sazonal (Quente vs Frio vs Neutro)</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleForceSnapshot}
                    disabled={savingSnapshot || !vendasData?.sales_by_customer?.length}
                    className="text-xs h-8"
                    data-testid="btn-snapshot-manual"
                  >
                    {savingSnapshot ? 'A gravar...' : 'Gravar Snapshot Agora'}
                  </Button>
                </div>
                {colorTemperatureHistory.length < 2 ? (
                  <div className="text-center py-8">
                    <CalendarBlank size={28} className="mx-auto text-stone-300 mb-2" />
                    <p className="text-sm text-stone-400">
                      Sem dados suficientes ainda. O histórico é construído automaticamente a cada carregamento de vendas.
                    </p>
                    <p className="text-xs text-stone-400 mt-1">
                      Trimestre{colorTemperatureHistory.length !== 1 ? 's' : ''} registado{colorTemperatureHistory.length !== 1 ? 's' : ''} até agora: <span className="font-semibold text-stone-500">{colorTemperatureHistory.length}</span> {colorTemperatureHistory.length > 0 && `(${colorTemperatureHistory.map(s => s.label).join(', ')})`} · são precisos pelo menos 2 para mostrar uma tendência.
                    </p>
                  </div>
                ) : (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={colorTemperatureHistory} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 11 }} />
                        <YAxis tickFormatter={formatShortCurrency} tick={{ fill: '#6b7280', fontSize: 11 }} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="quente_sales" name="Quente" fill={TEMPERATURE_META.Quente.color} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="frio_sales" name="Frio" fill={TEMPERATURE_META.Frio.color} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="neutro_sales" name="Neutro" fill={TEMPERATURE_META.Neutro.color} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quarterly Comparison Chart */}
          {quarterlyData.length > 0 && (
            <div className="gresart-card">
              <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <CalendarBlank size={20} className="text-[#009392]" />
                Comparativo Trimestral
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Bar Chart - double bars: Vendas (green) vs M2 (teal), side by side */}
                <div className="h-64 min-h-[256px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={quarterlyData} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="quarter" 
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        axisLine={{ stroke: '#d1d5db' }}
                      />
                      <YAxis 
                        yAxisId="sales"
                        tickFormatter={formatShortCurrency}
                        tick={{ fill: '#009392', fontSize: 12 }}
                        axisLine={{ stroke: '#d1d5db' }}
                      />
                      {hasQuarterlyM2 && (
                        <YAxis 
                          yAxisId="m2"
                          orientation="right"
                          tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                          tick={{ fill: '#f59e0b', fontSize: 12 }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                      )}
                      <Tooltip 
                        formatter={(value, name) => [
                          name === 'M2' ? `${value.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²` : formatCurrency(value),
                          name
                        ]}
                        labelFormatter={(label) => `Trimestre ${label}`}
                      />
                      {hasQuarterlyM2 && <Legend />}
                      <Bar yAxisId="sales" dataKey="sales" fill="#009392" radius={[4, 4, 0, 0]} name="Vendas" />
                      {hasQuarterlyM2 && <Bar yAxisId="m2" dataKey="m2" fill="#f59e0b" radius={[4, 4, 0, 0]} name="M2" />}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Quarterly Summary Cards */}
                <div className="grid grid-cols-2 gap-3">
                  {quarterlyData.map((q, idx) => (
                    <div 
                      key={q.quarter} 
                      className="p-4 rounded-xl border-2 transition-all cursor-pointer hover:shadow-md"
                      style={{ 
                        borderColor: QUARTER_COLORS[idx],
                        backgroundColor: `${QUARTER_COLORS[idx]}10`
                      }}
                      onClick={() => setSelectedPeriod(q.quarter)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-lg" style={{ color: QUARTER_COLORS[idx] }}>
                          {q.quarter}
                        </span>
                        <span className="text-xs text-stone-500">{q.months} meses</span>
                      </div>
                      <p className="text-lg font-bold text-stone-800">{formatCurrency(q.sales)}</p>
                      {q.hasM2 && (
                        <p className="text-sm font-medium text-amber-600 mt-0.5">{q.m2.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Monthly Sales Chart - unified: Vendas (€) + M2 side by side per month, so volume
              vs. faturação can be compared at a glance */}
          {filteredMonthlyData.length > 0 && (
            <div className="gresart-card">
              <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <Calendar size={20} className="text-[#009392]" />
                Vendas e M2 por Mês {selectedPeriod !== 'all' && <span className="text-sm font-normal text-stone-500">({selectedPeriod})</span>}
              </h3>
              {previsaoFecho && (
                <div className="mb-4 p-3 rounded-lg bg-teal-50 border border-teal-100" data-testid="previsao-fecho-banner">
                  {previsaoFecho.isEarlyMonth ? (
                    <>
                      <p className="text-sm font-semibold text-stone-600" data-testid="previsao-fecho-early-month">
                        Ainda é muito início do mês para uma previsão fiável · Faltam {formatCurrency(previsaoFecho.remainingToTarget)} para atingir o Objetivo
                      </p>
                      <p className="text-xs text-stone-500 mt-0.5">
                        Dia {previsaoFecho.currentMonthDay} de {previsaoFecho.currentMonthTotalDays} dias úteis do mês · Vendas até agora: {formatCurrency(previsaoFecho.currentMonthSales)}
                        {previsaoFecho.targetSource === 'manual' ? ' · Objetivo definido em Configurações' : ' · Objetivo do ficheiro Monthly Sales'}
                        . A previsão de fecho aparece a partir do 6º dia útil do mês.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-teal-700">
                        Previsão de Fecho: {formatCurrency(previsaoFecho.projectedClose)} ({previsaoFecho.pctOfTarget.toFixed(1)}% do Objetivo)
                      </p>
                      <p className="text-xs text-teal-600 mt-0.5">
                        Dia {previsaoFecho.currentMonthDay} de {previsaoFecho.currentMonthTotalDays} dias úteis do mês
                        {previsaoFecho.targetSource === 'manual' ? ' · Objetivo definido em Configurações' : ' · Objetivo do ficheiro Monthly Sales'}
                        {previsaoFecho.dailyDeficit != null && previsaoFecho.remainingDays > 0 && (
                          <> · Défice Diário: precisa vender {formatCurrency(previsaoFecho.dailyDeficit)}/dia útil nos {previsaoFecho.remainingDays} dias restantes para atingir o Objetivo</>
                        )}
                        {previsaoFecho.remainingDays <= 0 && previsaoFecho.remainingToTarget > 0 && (
                          <> · Faltam {formatCurrency(previsaoFecho.remainingToTarget)} e não há mais dias úteis este mês</>
                        )}
                      </p>
                    </>
                  )}
                </div>
              )}
              <div className="h-80 min-h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={filteredMonthlyData} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      axisLine={{ stroke: '#d1d5db' }}
                    />
                    <YAxis 
                      yAxisId="sales"
                      tickFormatter={formatShortCurrency}
                      tick={{ fill: '#009392', fontSize: 12 }}
                      axisLine={{ stroke: '#d1d5db' }}
                    />
                    {hasMonthlyM2 && (
                      <YAxis 
                        yAxisId="m2"
                        orientation="right"
                        tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                        tick={{ fill: '#f59e0b', fontSize: 12 }}
                        axisLine={{ stroke: '#d1d5db' }}
                      />
                    )}
                    <Tooltip 
                      formatter={(value, name) => [
                        name === 'M2' ? `${value.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²` : formatCurrency(value),
                        name
                      ]}
                    />
                    {hasMonthlyM2 && <Legend />}
                    <Bar 
                      yAxisId="sales"
                      dataKey="sales" 
                      name="Vendas" 
                      fill="#009392" 
                      radius={[4, 4, 0, 0]}
                    />
                    {hasMonthlyM2 && (
                      <Bar
                        yAxisId="m2"
                        dataKey="m2"
                        name="M2"
                        fill="#f59e0b"
                        radius={[4, 4, 0, 0]}
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {!hasMonthlyM2 && (
                <p className="text-xs text-stone-400 mt-2 text-center">
                  O ficheiro de vendas mensais carregado ainda não inclui m² por mês - apenas vendas (€) disponível.
                </p>
              )}
              {hasMonthlyM2 && isMonthlyM2Estimated && (
                <p className="text-xs text-stone-400 mt-2 text-center">
                  * M2 estimado por mês (distribuído proporcionalmente à faturação mensal, com base no total real de m² YTD)
                </p>
              )}

              {/* Monthly Sales Table */}
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b border-stone-200">
                      <th className="text-left py-2 px-3 font-medium text-stone-600">Mês</th>
                      <th className="text-right py-2 px-3 font-medium text-stone-600">Vendas</th>
                      {hasMonthlyM2 && <th className="text-right py-2 px-3 font-medium text-teal-600">M2</th>}
                      {(hasMonthlyM2 || hasAvgPriceReported) && <th className="text-right py-2 px-3 font-medium text-stone-600">Preço Médio{(!hasAvgPriceReported && isMonthlyM2Estimated) ? '*' : ''}</th>}
                      {hasPctFirstChoice && <th className="text-right py-2 px-3 font-medium text-[#009392]">% 1ª Escolha</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMonthlyData.map((item, idx) => (
                      <tr key={idx} className="border-b border-stone-100 hover:bg-stone-50">
                        <td className="py-2 px-3 font-medium text-stone-700">{item.month}</td>
                        <td className="py-2 px-3 text-right text-stone-600">{formatCurrency(item.sales)}</td>
                        {hasMonthlyM2 && (
                          <td className="py-2 px-3 text-right text-amber-600">
                            {item.m2 != null ? `${item.m2.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²` : '—'}
                          </td>
                        )}
                        {(hasMonthlyM2 || hasAvgPriceReported) && (
                          <td className="py-2 px-3 text-right text-stone-600" data-testid={`preco-medio-mes-${idx}`}>
                            {item.avg_price_reported != null
                              ? `${item.avg_price_reported.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/m²`
                              : (item.m2 ? `${(item.sales / item.m2).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/m²` : '—')}
                          </td>
                        )}
                        {hasPctFirstChoice && (
                          <td className="py-2 px-3 text-right text-[#009392] font-medium" data-testid={`pct-1a-escolha-mes-${idx}`}>
                            {item.pct_first_choice != null ? `${item.pct_first_choice.toLocaleString('pt-PT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` : '—'}
                          </td>
                        )}
                      </tr>
                    ))}
                    <tr className="bg-stone-50 font-bold">
                      <td className="py-2 px-3 text-stone-800">TOTAL {selectedPeriod !== 'all' ? selectedPeriod : ''}</td>
                      <td className="py-2 px-3 text-right text-[#009392]">{formatCurrency(filteredTotals.totalSales)}</td>
                      {hasMonthlyM2 && (
                        <td className="py-2 px-3 text-right text-amber-600">
                          {filteredMonthlyData.reduce((sum, m) => sum + (m.m2 || 0), 0).toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²
                        </td>
                      )}
                      {(hasMonthlyM2 || hasAvgPriceReported) && (
                        <td className="py-2 px-3 text-right text-stone-600" data-testid="preco-medio-total">
                          {(() => {
                            if (filteredTotals.avgPriceReported != null) {
                              return `${filteredTotals.avgPriceReported.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/m²`;
                            }
                            const totalM2 = filteredMonthlyData.reduce((sum, m) => sum + (m.m2 || 0), 0);
                            return totalM2 ? `${(filteredTotals.totalSales / totalM2).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/m²` : '—';
                          })()}
                        </td>
                      )}
                      {hasPctFirstChoice && (
                        <td className="py-2 px-3 text-right text-[#009392]" data-testid="pct-1a-escolha-total">
                          {filteredTotals.pctFirstChoice != null ? `${filteredTotals.pctFirstChoice.toLocaleString('pt-PT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` : '—'}
                        </td>
                      )}
                    </tr>
                  </tbody>
                </table>
                {!hasAvgPriceReported && hasMonthlyM2 && isMonthlyM2Estimated && (
                  <p className="text-xs text-stone-400 mt-2">
                    * Preço Médio calculado com o M2 estimado acima - por ser proporcional à faturação, dá sempre a mesma média global em todos os meses, não o preço real desse mês específico. Só varia mês a mês quando carregar um ficheiro com M2 real por mês.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Monthly Trend Line Chart */}
          {filteredMonthlyData.length > 1 && (
            <div className="gresart-card">
              <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <ChartLineUp size={20} className="text-[#009392]" />
                Tendência Mensal (Linha)
              </h3>
              <div className="h-72 min-h-[288px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={filteredMonthlyData} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      axisLine={{ stroke: '#d1d5db' }}
                    />
                    <YAxis 
                      yAxisId="sales"
                      tickFormatter={formatShortCurrency}
                      tick={{ fill: '#009392', fontSize: 12 }}
                      axisLine={{ stroke: '#d1d5db' }}
                    />
                    {hasMonthlyM2 && (
                      <YAxis 
                        yAxisId="m2"
                        orientation="right"
                        tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                        tick={{ fill: '#f59e0b', fontSize: 12 }}
                        axisLine={{ stroke: '#d1d5db' }}
                      />
                    )}
                    <Tooltip 
                      formatter={(value, name) => [
                        name === 'M2' ? `${value.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²` : formatCurrency(value),
                        name
                      ]}
                    />
                    {hasMonthlyM2 && <Legend />}
                    <Line yAxisId="sales" type="monotone" dataKey="sales" name="Vendas" stroke="#009392" strokeWidth={3} dot={{ r: 4, fill: '#009392' }} activeDot={{ r: 6 }} data-testid="monthly-trend-line" />
                    {hasMonthlyM2 && (
                      <Line yAxisId="m2" type="monotone" dataKey="m2" name="M2" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3, fill: '#f59e0b' }} />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* No data message */}
          {(!vendasData.monthly_sales || vendasData.monthly_sales.length === 0) && 
           (!vendasData.sales_by_collection || vendasData.sales_by_collection.length === 0) && (
            <div className="gresart-card text-center py-8">
              <Warning size={48} className="mx-auto text-amber-500 mb-3" />
              <p className="text-stone-600 font-medium">Não foi possível extrair dados dos ficheiros</p>
              <p className="text-stone-400 text-sm mt-1">
                Certifique-se de que carregou os ficheiros GPP corretos (Monthly_Sales.pdf, Hit_Parade.xlsx)
              </p>
            </div>
          )}

          {/* Sem Encomendas Ativas (Semáforo de Arrefecimento) e o filtro de Cliente Inativo
              foram removidos - a lógica automática (30 dias sem venda/pedido) não era
              fiável dado o fluxo de carga diária das Encomendas Pendentes. Os restantes
              indicadores (Stock, Aceleração, Concentração, Encomendas Zombis) mantêm-se. */}


          {/* Insights Comerciais: Tendência de Produto, Qualidade de Venda, Risco de
              Concentração e Encomendas Zombis - cruzam Hit Parade com Open Orders/Encomendas.
              Movido para o fundo da página, logo acima do Mapa de Calor, para dar prioridade
              visual aos gráficos de vendas/tendências no topo. */}
          {vendasData.insights && (
            <div className="gresart-card" data-testid="insights-comerciais-section">
              <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <Target size={20} className="text-[#009392]" />
                Insights Comerciais
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Tendência de Produto: Top Coleções */}
                <div className="rounded-xl border border-stone-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-stone-700 flex items-center gap-1.5">
                      <TrendUp size={16} className="text-[#009392]" />
                      Tendência de Produto — Top Coleções
                    </h4>
                    {(vendasData.insights.all_collections_trend?.length > 5) && (
                      <button
                        type="button"
                        onClick={() => setShowFullCollectionsRanking(true)}
                        className="text-xs font-medium text-[#009392] hover:underline flex-shrink-0"
                        data-testid="btn-ver-ranking-completo"
                      >
                        Ver ranking completo
                      </button>
                    )}
                  </div>
                  {(vendasData.insights.top_growing_collections?.length > 0 || vendasData.insights.top_declining_collections?.length > 0) ? (
                    <div className="space-y-1.5" data-testid="top-collections-trend-list">
                      {(vendasData.insights.top_growing_collections || []).map((c, i) => (
                        <div key={`up-${i}`} className="flex items-center justify-between text-sm" data-testid={`collection-trend-up-${i}`}>
                          <span className="text-stone-600 truncate">{c.collection}</span>
                          <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                            <TrendUp size={13} weight="bold" /> +{c.yoy_change}%
                          </span>
                        </div>
                      ))}
                      {(vendasData.insights.top_declining_collections || []).map((c, i) => (
                        <div key={`down-${i}`} className="flex items-center justify-between text-sm" data-testid={`collection-trend-down-${i}`}>
                          <span className="text-stone-600 truncate">{c.collection}</span>
                          <span className="inline-flex items-center gap-1 text-red-500 font-medium">
                            <TrendDown size={13} weight="bold" /> {c.yoy_change}%
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-stone-400">Sem dados suficientes para comparar com o ano anterior.</p>
                  )}
                </div>

                {/* Qualidade de Venda: AVG PR faturado vs pendente */}
                <div className="rounded-xl border border-stone-200 p-4">
                  <h4 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-1.5">
                    <Target size={16} className="text-[#009392]" />
                    Qualidade de Venda (Preço Médio)
                  </h4>
                  {vendasData.insights.quality_of_sale?.avg_price_invoiced != null && vendasData.insights.quality_of_sale?.avg_price_pending != null ? (
                    <div data-testid="quality-of-sale-global">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-stone-500">Faturado (já vendido)</span>
                        <span className="font-semibold text-stone-700">{formatCurrency(vendasData.insights.quality_of_sale.avg_price_invoiced)}/m²</span>
                      </div>
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-stone-500">Em Carteira (pendente)</span>
                        <span className="font-semibold text-stone-700">{formatCurrency(vendasData.insights.quality_of_sale.avg_price_pending)}/m²</span>
                      </div>
                      {vendasData.insights.quality_of_sale.diff_pct != null && (
                        <p className={`text-xs font-medium ${vendasData.insights.quality_of_sale.diff_pct >= 0 ? 'text-green-600' : 'text-amber-600'}`}>
                          {vendasData.insights.quality_of_sale.diff_pct >= 0
                            ? `A carteira pendente é ${Math.abs(vendasData.insights.quality_of_sale.diff_pct)}% mais cara que o já faturado — a vender material mais caro.`
                            : `A carteira pendente é ${Math.abs(vendasData.insights.quality_of_sale.diff_pct)}% mais barata que o já faturado — a vender material mais barato.`}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-stone-400">Sem carteira pendente carregada para comparar.</p>
                  )}
                </div>
              </div>

              {/* Risco de Concentração */}
              {vendasData.insights.concentration_risk_clients?.length > 0 && (
                <div className="mt-4 rounded-lg border border-purple-200 bg-purple-50 px-3 sm:px-4 py-3" data-testid="concentration-risk-banner">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-purple-700 font-semibold text-sm">
                      <Scales size={16} weight="fill" />
                      Risco de Concentração/Dependência
                    </div>
                    {(vendasData.insights.all_customers_concentration?.length > 0) && (
                      <button
                        type="button"
                        onClick={() => setShowFullConcentrationRanking(true)}
                        className="text-xs font-medium text-purple-700 hover:underline flex-shrink-0"
                        data-testid="btn-ver-ranking-completo-concentracao"
                      >
                        Ver ranking completo
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-purple-600 mt-1">Estes clientes representam, cada um, mais de 20% das vendas totais faturadas — dependência excessiva de poucos clientes.</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {vendasData.insights.concentration_risk_clients.map((c, i) => (
                      <span key={i} className="text-xs bg-white border border-purple-200 text-purple-700 rounded-full px-2.5 py-1" data-testid={`concentration-risk-client-${i}`}>
                        {displayCustomerName(c)} — {c.concentration_pct}%
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Ranking Completo de Clientes por Risco de Concentração - TODOS os clientes
                  com % de vendas totais, não apenas os que ultrapassam os 20%. */}
              <Dialog open={showFullConcentrationRanking} onOpenChange={setShowFullConcentrationRanking}>
                <DialogContent className="max-w-lg" data-testid="dialog-ranking-completo-concentracao">
                  <DialogHeader>
                    <DialogTitle>Ranking Completo — Risco de Concentração</DialogTitle>
                    <DialogDescription>Todos os clientes da carteira, ordenados pela % que representam do total de vendas faturadas.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-1.5 max-h-[60vh] overflow-y-auto py-1" data-testid="full-concentration-ranking-list">
                    {(vendasData?.insights?.all_customers_concentration || []).map((c, i) => (
                      <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-stone-100 last:border-0" data-testid={`full-concentration-row-${i}`}>
                        <span className="text-stone-600 truncate">{displayCustomerName(c)}</span>
                        <span className={`font-medium flex-shrink-0 ml-3 ${c.concentration_pct > 20 ? 'text-purple-600' : 'text-stone-500'}`}>
                          {c.concentration_pct}%
                        </span>
                      </div>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>


            </div>
          )}

          {/* Ranking Completo de Tendência de Produto - todas as coleções com yoy_change,
              não apenas as top/piores 5 mostradas no card acima. */}
          <Dialog open={showFullCollectionsRanking} onOpenChange={setShowFullCollectionsRanking}>
            <DialogContent className="max-w-lg" data-testid="dialog-ranking-completo-colecoes">
              <DialogHeader>
                <DialogTitle>Ranking Completo — Tendência de Produto</DialogTitle>
                <DialogDescription>Todas as coleções da carteira, ordenadas da maior crescimento à maior queda vs. ano anterior.</DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5 max-h-[60vh] overflow-y-auto py-1" data-testid="full-collections-ranking-list">
                {(vendasData?.insights?.all_collections_trend || []).map((c, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-stone-100 last:border-0" data-testid={`full-ranking-row-${i}`}>
                    <span className="text-stone-600 truncate">{c.collection}</span>
                    <span className={`inline-flex items-center gap-1 font-medium flex-shrink-0 ml-3 ${c.yoy_change >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {c.yoy_change >= 0 ? <TrendUp size={13} weight="bold" /> : <TrendDown size={13} weight="bold" />}
                      {c.yoy_change >= 0 ? '+' : ''}{c.yoy_change}%
                    </span>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>

          {/* Filtro por Temperatura: lista exata de clientes/materiais Quente/Frio/Neutro */}
          <Dialog open={!!selectedTemperature} onOpenChange={(open) => !open && setSelectedTemperature(null)}>
            <DialogContent className="max-w-2xl" data-testid="dialog-temperatura-detalhe">
              {selectedTemperature && (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded-full ${(TEMPERATURE_META[selectedTemperature] || TEMPERATURE_META.Neutro).badge}`}
                      >
                        {selectedTemperature}
                      </span>
                      Clientes e Materiais
                    </DialogTitle>
                    <DialogDescription>
                      {temperatureDetailItems.length} artigo{temperatureDetailItems.length !== 1 ? 's' : ''} de {new Set(temperatureDetailItems.map(i => i.customerName)).size} cliente{new Set(temperatureDetailItems.map(i => i.customerName)).size !== 1 ? 's' : ''}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
                    {temperatureDetailItems.length === 0 ? (
                      <p className="text-sm text-stone-400 text-center py-8">Nenhum material nesta temperatura de cor.</p>
                    ) : (
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-white">
                          <tr className="text-stone-500 border-b border-stone-200">
                            <th className="text-left px-2 py-2 font-semibold">Cliente</th>
                            <th className="text-left px-2 py-2 font-semibold">Material</th>
                            <th className="text-right px-2 py-2 font-semibold">M2</th>
                            <th className="text-right px-2 py-2 font-semibold">Vendas</th>
                          </tr>
                        </thead>
                        <tbody>
                          {temperatureDetailItems.map((item) => (
                            <tr key={item.rowKey} className="border-b border-stone-50" data-testid={`temperatura-detalhe-row-${item.rowKey}`}>
                              <td className="px-2 py-2 font-medium text-stone-800 whitespace-nowrap">{item.customerName}</td>
                              <td className="px-2 py-2 text-stone-600">{cleanProductName(item.material)}</td>
                              <td className="px-2 py-2 text-right text-teal-600 whitespace-nowrap">{item.qty != null ? `${item.qty.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²` : '—'}</td>
                              <td className="px-2 py-2 text-right font-semibold text-stone-700 whitespace-nowrap">{formatCurrency(item.sales)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Info Card - Show when no data */}
      {!loading && !vendasData && (
        <div className="gresart-card bg-gradient-to-r from-[#009392]/5 to-transparent border-[#009392]/20">
          <h3 className="font-medium text-stone-800 mb-2">Ficheiros suportados:</h3>
          <ul className="text-sm text-stone-600 space-y-1">
            <li className="flex items-center gap-2">
              <FilePdf size={16} className="text-red-500" />
              <strong>GPP_Monthly_Sales</strong> - Vendas mensais
            </li>
            <li className="flex items-center gap-2">
              <FileXls size={16} className="text-green-600" />
              <strong>GPP_Hit_Parade_Cust_Coll_Mat</strong> - Vendas por série e cliente
            </li>
            <li className="flex items-center gap-2">
              <FilePdf size={16} className="text-red-500" />
              <strong>GPP_Hit_Parade_Coll</strong> - Ranking de séries
            </li>
            <li className="flex items-center gap-2">
              <FilePdf size={16} className="text-red-500" />
              <strong>GPP_Hit_Parade_Cust</strong> - Ranking de clientes
            </li>
          </ul>
        </div>
      )}

      {/* Heat Map by Zone - always visible, independent of GPP sales data. Moved to the bottom of the page. */}
      {!loading && (
        <div className="gresart-card" data-testid="heatmap-zone-card">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
              <MapPin size={20} className="text-[#009392]" />
              Mapa de Calor - Vendas por Distrito
            </h3>
            <Button
              onClick={loadHeatmap}
              disabled={loadingHeatmap}
              size="sm"
              variant="outline"
              data-testid="btn-refresh-heatmap"
            >
              <ArrowsClockwise size={16} className={loadingHeatmap ? "animate-spin mr-1" : "mr-1"} />
              Atualizar
            </Button>
          </div>
          <p className="text-xs text-stone-400 mb-4">
            Vendas agrupadas pelos 7 distritos principais (clique num distrito para ver os clientes)
            {heatmapTotals.total > 0 && ` — ${heatmapTotals.matched} de ${heatmapTotals.total} clientes mapeados`}
          </p>
          {heatmapData.length === 0 ? (
            <div className="text-center py-6" data-testid="heatmap-empty-state">
              <Warning size={32} className="mx-auto text-amber-500 mb-2" />
              <p className="text-stone-500 text-sm">Sem correspondências entre clientes e vendas por distrito.</p>
              <p className="text-stone-400 text-xs mt-1">Verifique se os clientes têm o Código Postal (ou a cidade) preenchido na ficha.</p>
            </div>
          ) : (
            <div className="overflow-x-auto pb-1">
              <div
                className="grid gap-2 sm:gap-3 min-w-fit"
                style={{ gridTemplateColumns: `repeat(${heatmapData.length}, minmax(110px, 1fr))` }}
              >
                {(() => {
                  const maxSales = Math.max(...heatmapData.map(z => z.sales), 1);
                  const heatColor = '#16a34a';
                  return heatmapData.map((z, idx) => {
                    const ratio = z.sales / maxSales;
                    const bgAlpha = Math.round((15 + ratio * 25)).toString(16).padStart(2, '0');
                    return (
                      <button
                        key={z.concelho}
                        type="button"
                        onClick={() => toast.info(`${z.concelho}: ${z.client_count} cliente(s)`, {
                          description: (z.clients || []).join(', ')
                        })}
                        className="text-left rounded-2xl p-3 sm:p-4 border-2 transition-transform hover:scale-[1.02] cursor-pointer"
                        style={{ borderColor: heatColor, backgroundColor: `${heatColor}${bgAlpha}` }}
                        data-testid={`heatmap-zone-${idx}`}
                      >
                        <p className="text-xs sm:text-sm font-semibold uppercase tracking-wide truncate" style={{ color: heatColor }}>{z.concelho}</p>
                        <p className="text-lg sm:text-xl font-bold text-stone-800 mt-1">{formatShortCurrency(z.sales)}</p>
                        <p className="text-xs text-stone-500 mt-1">{z.client_count} cliente(s)</p>
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* Informe de Errores: clients with real sales this year that couldn't be placed on
              the map, with the exact reason so the admin can fix the client's data directly. */}
          {heatmapUnmatched.length > 0 && (
            <div className="mt-4 pt-4 border-t border-stone-200">
              <button
                type="button"
                onClick={() => setShowUnmatchedClients(!showUnmatchedClients)}
                className="flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700"
                data-testid="btn-toggle-unmatched-clients"
              >
                <Warning size={16} />
                {heatmapUnmatched.length} cliente(s) fora do mapa
                <CaretDown size={14} className={showUnmatchedClients ? "rotate-180 transition-transform" : "transition-transform"} />
              </button>
              {showUnmatchedClients && (
                <div className="mt-3 space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="unmatched-clients-list">
                  {heatmapUnmatched.map((u, idx) => (
                    <div key={idx} className="p-2.5 rounded-lg bg-amber-50 border border-amber-200 flex items-start justify-between gap-2" data-testid={`unmatched-client-${idx}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-stone-700 truncate">{u.customer}</p>
                        <p className="text-xs text-amber-700 mt-0.5">{u.reason}</p>
                        {(u.city || u.postal_code) && (
                          <p className="text-xs text-stone-400 mt-0.5">
                            Ficha: {u.postal_code || '—'} {u.city || ''}
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-stone-500 whitespace-nowrap flex-shrink-0">{formatCurrency(u.sales)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* "Desempenho Anual por Cliente" - cruza os PDFs 2024/2025 (€ e M2) com o YTD de 2026.
          Faça upload de "vendas em € 2024 - 2025.pdf" e "Vendas em M2 2024 - 2025.pdf" na
          mesma zona de upload de Configurações > Vendas, usada para o Monthly Sales. */}
      <div className="gresart-card mt-6" data-testid="desempenho-anual-card">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
            <Scales size={20} className="text-[#009392]" />
            Desempenho Anual por Cliente
          </h3>
          <Button onClick={loadAnnualPerformance} variant="outline" size="sm" className="gap-1.5" data-testid="btn-refresh-desempenho-anual">
            <ArrowsClockwise size={14} className={loadingAnnualPerformance ? "animate-spin" : ""} />
            Atualizar
          </Button>
        </div>
        <p className="text-xs text-stone-400 mb-3">Compara 2024, 2025 e o YTD de 2026, e mostra o quanto falta para igualar o melhor ano.</p>

        {loadingAnnualPerformance ? (
          <div className="flex items-center justify-center py-10">
            <LoadingSpinner size={32} />
          </div>
        ) : !annualPerformance.has_eur_data && !annualPerformance.has_m2_data ? (
          <p className="text-sm text-stone-400 text-center py-8" data-testid="desempenho-anual-empty">
            Ainda sem dados. Faça upload de "vendas em € 2024 - 2025.pdf" e "Vendas em M2 2024 - 2025.pdf" em Configurações &gt; Vendas.
          </p>
        ) : (
          <>
            <div className="relative mb-3 max-w-sm">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                value={annualPerfSearch}
                onChange={(e) => setAnnualPerfSearch(e.target.value)}
                placeholder="Pesquisar cliente..."
                className="pl-9"
                data-testid="desempenho-anual-search"
              />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="desempenho-anual-table">
                <thead>
                  <tr className="border-b border-stone-200">
                    <SortableTh label="Nome" sortKey="name" sortState={annualPerfSort} onSort={toggleAnnualPerfSort} colorClass="text-stone-600" testId="th-sort-nome-anual" align="left" />
                    <SortableTh label="2024 (€)" sortKey="sales_2024" sortState={annualPerfSort} onSort={toggleAnnualPerfSort} colorClass="text-stone-500" testId="th-sort-2024-eur" />
                    <SortableTh label="2025 (€)" sortKey="sales_2025" sortState={annualPerfSort} onSort={toggleAnnualPerfSort} colorClass="text-stone-500" testId="th-sort-2025-eur" />
                    <SortableTh label="2026 YTD (€)" sortKey="sales_2026_ytd" sortState={annualPerfSort} onSort={toggleAnnualPerfSort} colorClass="text-[#009392]" testId="th-sort-2026ytd-eur" />
                    <SortableTh label="2024 (M2)" sortKey="m2_2024" sortState={annualPerfSort} onSort={toggleAnnualPerfSort} colorClass="text-teal-600" testId="th-sort-2024-m2" />
                    <SortableTh label="2025 (M2)" sortKey="m2_2025" sortState={annualPerfSort} onSort={toggleAnnualPerfSort} colorClass="text-teal-600" testId="th-sort-2025-m2" />
                    <SortableTh label="2026 YTD (M2)" sortKey="m2_2026_ytd" sortState={annualPerfSort} onSort={toggleAnnualPerfSort} colorClass="text-teal-600" testId="th-sort-2026ytd-m2" />
                    <SortableTh label="Falta p/ Máximo (€)" sortKey="falta_maximo" sortState={annualPerfSort} onSort={toggleAnnualPerfSort} colorClass="text-stone-600" testId="th-sort-falta-maximo" />
                  </tr>
                </thead>
                <tbody>
                  {filteredAnnualPerformance.map((c, idx) => (
                    <tr key={c.client_number || c.name || idx} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`desempenho-anual-row-${idx}`}>
                      <td className="py-2 px-1.5 sm:px-3 text-stone-700 font-medium max-w-[220px] truncate" title={toSentenceCase(c.name)}>{c.commercial_name || shortenCompanyName(c.name)}</td>
                      <td className="py-2 px-1.5 sm:px-3 text-right text-stone-500 whitespace-nowrap">{formatCurrency(c.sales_2024)}</td>
                      <td className="py-2 px-1.5 sm:px-3 text-right text-stone-500 whitespace-nowrap">{formatCurrency(c.sales_2025)}</td>
                      <td className="py-2 px-1.5 sm:px-3 text-right text-[#009392] font-semibold whitespace-nowrap">
                        {c.sales_2026_ytd != null ? formatCurrency(c.sales_2026_ytd) : '—'}
                      </td>
                      <td className="py-2 px-1.5 sm:px-3 text-right text-teal-600 whitespace-nowrap">
                        {c.m2_2024 != null ? `${c.m2_2024.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²` : '—'}
                      </td>
                      <td className="py-2 px-1.5 sm:px-3 text-right text-teal-600 whitespace-nowrap">
                        {c.m2_2025 != null ? `${c.m2_2025.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²` : '—'}
                      </td>
                      <td className="py-2 px-1.5 sm:px-3 text-right text-teal-600 whitespace-nowrap">
                        {c.m2_2026_ytd != null ? `${c.m2_2026_ytd.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²` : '—'}
                      </td>
                      <td className="py-2 px-1.5 sm:px-3 text-right whitespace-nowrap">
                        {c.falta_maximo != null ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                            c.falta_status === 'positive' ? 'bg-green-100 text-green-700' :
                            c.falta_status === 'warning' ? 'bg-orange-100 text-orange-700' :
                            'bg-red-100 text-red-700'
                          }`} data-testid={`falta-maximo-badge-${idx}`}>
                            {c.falta_maximo <= 0 ? 'Objetivo superado' : formatCurrency(c.falta_maximo)}
                          </span>
                        ) : (
                          <span className="text-stone-400 text-xs">Sem vendas 2026</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredAnnualPerformance.length === 0 && (
                    <tr>
                      <td colSpan={8} className="py-6 text-center text-stone-400 text-sm">Nenhum cliente encontrado.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
